
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiUrl, MCP_BASE, API_BASE } from '../../utils/api';
import { NodeData, EvaluationConfig, Node as WorkflowNode, Connection } from '../types';
import { MODEL_OPTIONS, MEMORY_OPTIONS, TOOL_AGENTS_REGISTRY, DEFAULT_EVALUATION_METRICS, EVALUATION_SYSTEM_INSTRUCTION } from '../constants';
import { 
  fetchMCPTools, 
  checkServiceAccount, 
  MCPTool,
  fetchToolAgents,
  ToolAgentInfo,
  fetchMemoryProviders,
  verifyMemoryProvider,
  getMemoryStatus,
  getMemoryEnvConfig,
  MemoryProvider,
  MemoryVerifyResult,
  MemoryEnvConfig
} from '../api';
export const WhatsAppIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.94 3.659 1.437 5.634 1.437h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const TelegramIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.14-.257.257-.527.257l.186-2.642 4.83-4.364c.21-.186-.045-.29-.324-.104l-5.97 3.76-2.564-.8c-.558-.174-.57-.558.117-.827l10.02-3.86c.463-.174.868.104.658 1.04z" />
  </svg>
);

export const SlackIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.263 0a2.527 2.527 0 0 1 2.522-2.52h6.313a2.528 2.528 0 0 1 0 5.053H8.827a2.528 2.528 0 0 1-2.522-2.533zM8.827 5.042a2.528 2.528 0 0 1-2.523-2.52A2.528 2.528 0 0 1 8.827 0a2.527 2.527 0 0 1 2.52 2.522v2.52h-2.52zm0 1.263a2.527 2.527 0 0 1-2.52-2.522V10.1c0 1.393 1.13 2.523 2.52 2.523s2.52-1.13 2.52-2.523V6.305zm10.131 3.785a2.528 2.528 0 0 1 2.52-2.523A2.528 2.528 0 0 1 24 8.827a2.527 2.527 0 0 1-2.522 2.52h-2.52V8.827zm-1.263 0a2.527 2.527 0 0 1-2.522 2.52h-6.313a2.528 2.528 0 0 1 0-5.053h6.313a2.528 2.528 0 0 1 2.522 2.533zm-2.556 10.131a2.528 2.528 0 0 1 2.523 2.52A2.528 2.528 0 0 1 15.14 24a2.527 2.527 0 0 1-2.52-2.522v-2.52h2.52zm0-1.263a2.527 2.527 0 0 1 2.52 2.522v-6.313c0-1.393-1.13-2.523-2.52-2.523s-2.52 1.13-2.52 2.523v6.313z" />
  </svg>
);

export const EmailIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

export const OutlookIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.4 24l11.4-2V2L11.4 0v24zM0 18.2l10 1.8V4L0 5.8v12.4z" />
  </svg>
);

// --- Icons ---
export const TrashIcon = () => <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
export const ChevronDown = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
export const ChevronUp = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>;
export const SearchIcon = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
export const CheckIcon = ({ className = "w-4 h-4" }: { className?: string }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>;
export const LoadingIcon = () => <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;

// Node Specific Icons
export const UserIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
export const CpuIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>;
export const BriefcaseIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
export const UsersIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
export const FlaskIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>;
export const RobotIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
export const DatabaseIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>;
export const PlayCircleIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
export const StopCircleIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>;
export const StoreIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
export const BoltIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;

export const Icons = {
  user: UserIcon,
  cpu: CpuIcon,
  briefcase: BriefcaseIcon,
  users: UsersIcon,
  flask: FlaskIcon,
  Flask: FlaskIcon,
  robot: RobotIcon,
  database: DatabaseIcon,
  store: StoreIcon,
  play_circle: PlayCircleIcon,
  stop_circle: StopCircleIcon,
  bolt: BoltIcon
};

// --- Styles ---
const inputClass = "w-full text-xs px-3 py-2 border border-slate-200 rounded hover:border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors text-slate-700 placeholder-slate-400 bg-white";
const labelClass = "block text-[10px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide";

const SectionHeader = ({ title, expanded, onToggle }: { title: string, expanded: boolean, onToggle: () => void }) => (
  <div onClick={onToggle} className="flex items-center justify-between py-2 cursor-pointer mt-3 border-t border-slate-100 group">
    <span className="text-xs font-semibold text-slate-600 group-hover:text-blue-600 transition-colors">{title}</span>
    <span className="text-slate-400 group-hover:text-blue-600">{expanded ? <ChevronUp /> : <ChevronDown />}</span>
  </div>
);

const InputGroup: React.FC<{ label: string, children?: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-4 last:mb-0">
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

// File attachment types for testing
const ALLOWED_TEST_TYPES = '.pdf,.docx,.doc,.txt,.mp3,.wav,.ogg,.png,.jpg,.jpeg,.gif,.webp';

// --- SHARED COMPONENT FOR 5 STANDARD FIELDS WITH TESTING ---
const StandardAgentFields = ({ data, onChange, readOnlyParams = false, nodeType = 'agent_engine' }: { 
  data: NodeData, 
  onChange: (d: Partial<NodeData>) => void, 
  readOnlyParams?: boolean,
  nodeType?: string 
}) => {
  const [testLoading, setTestLoading] = useState(false);
  const [testFile, setTestFile] = useState<{ name: string; type: string; base64: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Handle file selection for test
  const handleTestFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);
    
    if (!file) {
      setTestFile(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setFileError('Max 10MB');
      setTestFile(null);
      return;
    }

    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setTestFile({ name: file.name, type: file.type, base64 });
    } catch {
      setFileError('Failed to read file');
    }
  };

  const clearTestFile = () => {
    setTestFile(null);
    setFileError(null);
  };

  // Run test for this node
  const runNodeTest = async () => {
    if (!data.testInput?.trim() && !testFile) {
      onChange({ testOutput: '⚠️ Please enter test input or attach a file.' });
      return;
    }

    setTestLoading(true);
    onChange({ testOutput: 'Testing...' });

    try {
      const response = await fetch(apiUrl('/agent-builder/node/test'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_type: nodeType,
          node_data: data,
          input: data.testInput || '',
          file_data: testFile || undefined
        })
      });

      const result = await response.json();
      if (result.success) {
        onChange({ testOutput: result.output });
      } else {
        onChange({ testOutput: `❌ Error: ${result.error || 'Test failed'}` });
      }
    } catch (error) {
      onChange({ testOutput: `❌ Error: ${error}` });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <>
      <InputGroup label="Agent Name">
        <input 
          type="text" 
          className={inputClass}
          placeholder="Name your agent..."
          value={data.agentName || ''}
          onChange={(e) => onChange({ agentName: e.target.value })}
          disabled={readOnlyParams}
        />
      </InputGroup>
      
      <InputGroup label="Description">
        <textarea 
          className={`${inputClass} resize-none`}
          rows={2}
          placeholder="What does this agent do?"
          value={data.description || ''}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </InputGroup>

      <InputGroup label="System Instruction">
        <textarea 
          className={`${inputClass} font-mono`}
          rows={4}
          placeholder="You are a helpful assistant..."
          value={data.systemPrompt || ''}
          onChange={(e) => onChange({ systemPrompt: e.target.value })}
        />
      </InputGroup>

      <div className="border-t border-slate-100 my-4"></div>
      <div className="mb-2 text-[10px] font-bold text-blue-600 uppercase tracking-wide">Testing</div>

      <InputGroup label="Input">
        <textarea 
          className={`${inputClass} bg-blue-50/30 border-blue-100`}
          rows={2}
          placeholder="Enter test prompt or payload..."
          value={data.testInput || ''}
          onChange={(e) => onChange({ testInput: e.target.value })}
        />
      </InputGroup>

      {/* File Attachment for Test */}
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <label className="flex-1 flex items-center justify-center px-2 py-1.5 border border-dashed border-slate-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
            <input
              type="file"
              accept={ALLOWED_TEST_TYPES}
              onChange={handleTestFileSelect}
              className="hidden"
            />
            <span className="text-[10px] text-slate-500">
              {testFile ? (
                <span className="text-blue-600 flex items-center gap-1">📎 {testFile.name}</span>
              ) : (
                '📎 Attach file (optional)'
              )}
            </span>
          </label>
          {testFile && (
            <button onClick={clearTestFile} className="text-red-400 hover:text-red-600 text-xs">✕</button>
          )}
        </div>
        {fileError && <p className="text-[9px] text-red-500 mt-0.5">{fileError}</p>}
      </div>

      <InputGroup label="Output">
        <textarea 
          className={`${inputClass} bg-slate-50 text-slate-500`}
          rows={2}
          placeholder="Test results will appear here..."
          value={data.testOutput || ''}
          readOnly
        />
      </InputGroup>

      {/* Test Button */}
      <button
        onClick={runNodeTest}
        disabled={testLoading}
        className={`w-full mt-2 px-3 py-2 rounded text-xs font-semibold transition-all flex items-center justify-center gap-2 ${testLoading
          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
          : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
          }`}
      >
        {testLoading ? (
          <>
            <span className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
            Testing...
          </>
        ) : (
          <>🧪 Run Test</>
        )}
      </button>
    </>
  );
};


// --- NEW NODES ---

export const StartNode = () => (
  <div className="text-xs text-slate-500 italic">
    Entry point of the workflow. Connect to the first agent or task.
  </div>
);

export const EndNode = () => (
  <div className="text-xs text-slate-500 italic">
    Workflow termination point. Results will be collected here.
  </div>
);

export const TriggerNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = (text: string, platform: string) => {
    navigator.clipboard.writeText(text);
    setCopied(platform);
    setTimeout(() => setCopied(null), 2000);
  };

  // Support legacy single string or new array
  const platforms: string[] = Array.isArray(data.triggerPlatforms) 
    ? data.triggerPlatforms 
    : (data.triggerPlatform ? [data.triggerPlatform] : ['whatsapp']);

  const togglePlatform = (platformToToggle: string) => {
    const newPlatforms = platforms.includes(platformToToggle)
      ? platforms.filter(p => p !== platformToToggle)
      : [...platforms, platformToToggle];
    
    // Fallback to at least one platform if empty, or allow empty? Let's allow empty, but default to whatsapp if none.
    onChange({ triggerPlatforms: newPlatforms.length > 0 ? newPlatforms : ['whatsapp'] });
  };

  return (
    <div className="animate-fade-in">
      <InputGroup label="Integration Platforms">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => togglePlatform('whatsapp')}
            className={`flex flex-col items-center justify-center p-2 rounded-md border transition-all cursor-pointer ${
              platforms.includes('whatsapp')
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-bold'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
            title="WhatsApp"
          >
            <WhatsAppIcon className="w-5 h-5 mb-1" />
            <span className="text-[9px] font-semibold">WhatsApp</span>
          </button>

          <button
            onClick={() => togglePlatform('telegram')}
            className={`flex flex-col items-center justify-center p-2 rounded-md border transition-all cursor-pointer ${
              platforms.includes('telegram')
                ? 'bg-blue-50 border-blue-300 text-blue-700 font-bold'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
            title="Telegram"
          >
            <TelegramIcon className="w-5 h-5 mb-1" />
            <span className="text-[9px] font-semibold">Telegram</span>
          </button>

          <button
            onClick={() => togglePlatform('google_drive')}
            className={`flex flex-col items-center justify-center p-2 rounded-md border transition-all cursor-pointer ${
              platforms.includes('google_drive')
                ? 'bg-amber-50 border-amber-300 text-amber-700 font-bold'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
            title="Google Drive"
          >
            <svg viewBox="0 0 360 360" className="w-5 h-5 mb-1">
              <path d="M110 280l55-95h190l-55 95z" fill="#34A853"/>
              <path d="M250 280l-140-240h110l140 240z" fill="#4285F4"/>
              <path d="M165 185L220 90h110l-55 95z" fill="#FBBC05"/>
            </svg>
            <span className="text-[9px] font-semibold">Google Drive</span>
          </button>

          <button
            disabled
            className="flex flex-col items-center justify-center p-2 rounded-md border border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
            title="Slack (Coming Soon)"
          >
            <SlackIcon className="w-5 h-5 mb-1" />
            <span className="text-[9px] font-semibold">Slack</span>
          </button>

          <button
            disabled
            className="flex flex-col items-center justify-center p-2 rounded-md border border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
            title="Email (Coming Soon)"
          >
            <EmailIcon className="w-5 h-5 mb-1" />
            <span className="text-[9px] font-semibold">Email</span>
          </button>

          <button
            disabled
            className="flex flex-col items-center justify-center p-2 rounded-md border border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
            title="Outlook (Coming Soon)"
          >
            <OutlookIcon className="w-5 h-5 mb-1" />
            <span className="text-[9px] font-semibold">Outlook</span>
          </button>
        </div>
      </InputGroup>

      {platforms.includes('whatsapp') && (
        <div className="space-y-4 mt-4 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
          <div className="flex items-center gap-1.5 mb-2">
            <WhatsAppIcon className="w-4 h-4 text-emerald-600" />
            <span className="text-[10px] font-bold text-emerald-700 uppercase">WhatsApp Config</span>
          </div>
          <InputGroup label="Access Token">
            <input
              type="password"
              className={inputClass}
              placeholder="EAAB..."
              value={data.whatsappToken || ''}
              onChange={(e) => onChange({ whatsappToken: e.target.value })}
            />
          </InputGroup>
          <InputGroup label="Verify Token">
            <input
              type="text"
              className={inputClass}
              placeholder="my_verify_token"
              value={data.whatsappVerifyToken || ''}
              onChange={(e) => onChange({ whatsappVerifyToken: e.target.value })}
            />
          </InputGroup>
          <InputGroup label="Phone Number ID">
            <input
              type="text"
              className={inputClass}
              placeholder="1029384756"
              value={data.whatsappPhoneNumberId || ''}
              onChange={(e) => onChange({ whatsappPhoneNumberId: e.target.value })}
            />
          </InputGroup>
          
          <div className="mt-2 p-2 bg-white border border-emerald-100 rounded">
            <label className="block text-[9px] font-bold text-slate-500 mb-1 uppercase">WhatsApp Webhook URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                className={`${inputClass} bg-slate-50 cursor-default truncate`}
                value={`${API_BASE}/whatsapp/webhook`}
              />
              <button
                onClick={() => copyToClipboard(`${API_BASE}/whatsapp/webhook`, 'whatsapp')}
                className="px-2 py-1 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded hover:bg-emerald-100 transition-colors text-[10px] font-bold shrink-0"
              >
                {copied === 'whatsapp' ? 'COPIED!' : 'COPY'}
              </button>
            </div>
          </div>
        </div>
      )}

      {platforms.includes('telegram') && (
        <div className="space-y-4 mt-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-1.5 mb-2">
            <TelegramIcon className="w-4 h-4 text-blue-600" />
            <span className="text-[10px] font-bold text-blue-700 uppercase">Telegram Config</span>
          </div>
          <InputGroup label="Bot Token">
            <input
              type="password"
              className={inputClass}
              placeholder="123456:ABC-DEF..."
              value={data.telegramBotToken || ''}
              onChange={(e) => onChange({ telegramBotToken: e.target.value })}
            />
          </InputGroup>
          
          <div className="mt-2 p-2 bg-white border border-blue-100 rounded">
            <label className="block text-[9px] font-bold text-slate-500 mb-1 uppercase">Telegram Webhook URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                className={`${inputClass} bg-slate-50 cursor-default truncate`}
                value={`${API_BASE}/telegram/webhook`}
              />
              <button
                onClick={() => copyToClipboard(`${API_BASE}/telegram/webhook`, 'telegram')}
                className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-600 rounded hover:bg-blue-100 transition-colors text-[10px] font-bold shrink-0"
              >
                {copied === 'telegram' ? 'COPIED!' : 'COPY'}
              </button>
            </div>
          </div>
        </div>
      )}

      {platforms.includes('google_drive') && (
        <div className="space-y-4 mt-4 p-3 bg-amber-50/50 rounded-lg border border-amber-100 animate-fade-in">
          <div className="flex items-center gap-1.5 mb-2">
            <svg viewBox="0 0 360 360" className="w-4 h-4">
              <path d="M110 280l55-95h190l-55 95z" fill="#34A853"/>
              <path d="M250 280l-140-240h110l140 240z" fill="#4285F4"/>
              <path d="M165 185L220 90h110l-55 95z" fill="#FBBC05"/>
            </svg>
            <span className="text-[10px] font-bold text-amber-700 uppercase">Google Drive Config</span>
          </div>
          <InputGroup label="Folder ID to Monitor">
            <input
              type="text"
              className={inputClass}
              placeholder="Enter Google Drive Folder ID..."
              value={data.connectorTarget || ''}
              onChange={(e) => onChange({ connectorTarget: e.target.value })}
            />
          </InputGroup>
          <div className="text-[9px] text-slate-500 italic">
            Monitors this Google Drive folder for new medical PDF files. Once detected, the clinical analyzer workflow executes automatically.
          </div>
        </div>
      )}
    </div>
  );
};

export const MemoryNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [providers, setProviders] = useState<MemoryProvider[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<MemoryVerifyResult | null>(null);
  const [memoryStatus, setMemoryStatus] = useState<{available: boolean; provider: string | null} | null>(null);
  const [envConfig, setEnvConfig] = useState<MemoryEnvConfig | null>(null);
  const [useEnvConfig, setUseEnvConfig] = useState(true);

  // Fetch memory providers and env config on mount
  useEffect(() => {
    fetchMemoryProviders().then(setProviders);
    getMemoryEnvConfig().then(config => {
      setEnvConfig(config);
      // Auto-fill credentials from env if available
      if (config?.vertex_ai?.configured && !data.memoryCredentials?.project_id) {
        onChange({
          memoryCredentials: {
            ...data.memoryCredentials,
            project_id: config.vertex_ai.project_id,
            location: config.vertex_ai.location,
            agent_engine_id: config.vertex_ai.agent_engine_id,
            service_account_path: config.vertex_ai.service_account_path,
          }
        });
      }
    });
    getMemoryStatus().then(status => {
      setMemoryStatus({ available: status.available, provider: status.provider });
      // Auto-verify if using vertex_ai
      if (data.memoryType === 'vertex_ai' && status.available) {
        setVerifyResult({
          success: true,
          provider: 'vertex_ai',
          message: 'Vertex AI Memory Bank is configured and available',
          details: status.config
        });
      }
    });
  }, [data.memoryType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current provider config
  const currentProvider = providers.find(p => p.id === data.memoryType) || null;

  // Handle verification
  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await verifyMemoryProvider(
        data.memoryType || 'vertex_ai',
        data.memoryCredentials || {}
      );
      setVerifyResult(result);
      onChange({ memoryVerified: result.success });
    } catch (e) {
      setVerifyResult({
        success: false,
        provider: data.memoryType || 'vertex_ai',
        message: 'Verification failed'
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle credential change
  const handleCredentialChange = (key: string, value: string) => {
    onChange({
      memoryCredentials: {
        ...data.memoryCredentials,
        [key]: value
      }
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className={labelClass} style={{marginBottom:0}}>Memory Enabled</span>
        <button 
          onClick={() => onChange({ memoryEnabled: !data.memoryEnabled })}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition-colors ${data.memoryEnabled
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}
        >
          <div className={`w-1.5 h-1.5 rounded-full ${data.memoryEnabled ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
          {data.memoryEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <InputGroup label="Memory Provider">
        <select 
          className={inputClass}
          value={data.memoryType || 'vertex_ai'}
          onChange={(e) => {
            onChange({ memoryType: e.target.value as any, memoryVerified: false, memoryCredentials: {} });
            setVerifyResult(null);
          }}
        >
          {providers.length > 0 ? (
            providers.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} {p.cloud ? `(${p.cloud})` : ''}
              </option>
            ))
          ) : (
            Object.entries(MEMORY_OPTIONS).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))
          )}
        </select>
      </InputGroup>

      {currentProvider && (
        <p className="text-[10px] text-slate-500 mt-1 mb-3">
          {currentProvider.description}
        </p>
      )}

      {/* Cloud provider badge */}
      {currentProvider?.cloud && (
        <div className={`mb-3 inline-flex items-center px-2 py-1 rounded text-[10px] font-bold ${currentProvider.cloud === 'GCP' ? 'bg-blue-50 text-blue-700' :
          currentProvider.cloud === 'AWS' ? 'bg-orange-50 text-orange-700' :
          currentProvider.cloud === 'Azure' ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-50 text-slate-600'
        }`}>
          {currentProvider.cloud}
        </div>
      )}

      {/* Vertex AI Configuration Mode Toggle */}
      {data.memoryType === 'vertex_ai' && envConfig?.vertex_ai && (
        <div className="mb-3">
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => setUseEnvConfig(true)}
              className={`flex-1 px-2 py-1.5 rounded text-[10px] font-semibold transition-all ${useEnvConfig
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                }`}
            >
              Use Backend Config
            </button>
            <button
              onClick={() => setUseEnvConfig(false)}
              className={`flex-1 px-2 py-1.5 rounded text-[10px] font-semibold transition-all ${!useEnvConfig
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                }`}
            >
              Manual Entry
            </button>
          </div>
          
          {useEnvConfig && envConfig.vertex_ai.configured && memoryStatus?.available && (
            <div className="p-2 bg-emerald-50 border border-emerald-100 rounded text-[10px] text-emerald-700 flex items-center gap-2">
              <CheckIcon className="w-3.5 h-3.5" />
              <span><strong>Auto-configured</strong> from backend credentials</span>
            </div>
          )}
          
          {useEnvConfig && !envConfig.vertex_ai.configured && (
            <div className="p-2 bg-amber-50 border border-amber-100 rounded text-[10px] text-amber-700">
              <strong>Missing config:</strong> Some environment variables are not set in backend. 
              {!envConfig.vertex_ai.agent_engine_id && <span className="block mt-1">• AGENT_ENGINE_ID required</span>}
            </div>
          )}
        </div>
      )}

      {/* Show current Vertex AI config from env */}
      {data.memoryType === 'vertex_ai' && useEnvConfig && envConfig?.vertex_ai && (
        <div className="mb-3 p-3 bg-slate-50 rounded border border-slate-100 space-y-2">
          <div className="text-[10px] font-semibold text-slate-600 mb-2">Current Backend Configuration</div>
          <div className="grid grid-cols-2 gap-2 text-[9px]">
            <div>
              <span className="text-slate-400">Project:</span>
              <span className="ml-1 text-slate-600 font-mono">{envConfig.vertex_ai.project_id || 'Not set'}</span>
            </div>
            <div>
              <span className="text-slate-400">Location:</span>
              <span className="ml-1 text-slate-600 font-mono">{envConfig.vertex_ai.location || 'Not set'}</span>
            </div>
            <div className="col-span-2">
              <span className="text-slate-400">Agent Engine ID:</span>
              <span className={`ml-1 font-mono ${envConfig.vertex_ai.agent_engine_id ? 'text-slate-600' : 'text-amber-600'}`}>
                {envConfig.vertex_ai.agent_engine_id || 'Not set (Required)'}
              </span>
            </div>
            <div className="col-span-2">
              <span className="text-slate-400">Service Account:</span>
              <span className="ml-1 text-slate-600 font-mono truncate block">{envConfig.vertex_ai.service_account_path || 'Default credentials'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Auto-configured notice for GCP (legacy fallback) */}
      {currentProvider?.auto_configured && data.memoryType === 'vertex_ai' && memoryStatus?.available && !envConfig?.vertex_ai && (
        <div className="mb-3 p-2 bg-emerald-50 border border-emerald-100 rounded text-[10px] text-emerald-700 flex items-center gap-2">
          <CheckIcon className="w-3.5 h-3.5" />
          <span><strong>Auto-configured</strong> from backend credentials</span>
        </div>
      )}

      {/* Window size for buffer */}
      {data.memoryType === 'window_buffer' && (
        <InputGroup label="Window Size (Turns)">
          <input 
            type="number"
            className={inputClass}
            value={data.windowSize || 10}
            min={1}
            max={100}
            onChange={(e) => onChange({ windowSize: parseInt(e.target.value) || 10 })}
          />
        </InputGroup>
      )}

      {/* Manual Vertex AI Configuration */}
      {data.memoryType === 'vertex_ai' && !useEnvConfig && (
        <div className="mb-3 p-3 bg-slate-50 rounded border border-slate-100 space-y-3">
          <div className="text-[10px] font-semibold text-slate-600 mb-2">Vertex AI Configuration (Editable)</div>
          <InputGroup label="Project ID">
            <input 
              type="text"
              className={inputClass}
              placeholder="your-gcp-project-id"
              value={data.memoryCredentials?.project_id || ''}
              onChange={(e) => handleCredentialChange('project_id', e.target.value)}
            />
          </InputGroup>
          <InputGroup label="Location">
            <input 
              type="text"
              className={inputClass}
              placeholder="us-central1"
              value={data.memoryCredentials?.location || ''}
              onChange={(e) => handleCredentialChange('location', e.target.value)}
            />
          </InputGroup>
          <InputGroup label="Agent Engine ID (Required)">
            <input 
              type="text"
              className={inputClass}
              placeholder="Enter your Agent Engine ID"
              value={data.memoryCredentials?.agent_engine_id || ''}
              onChange={(e) => handleCredentialChange('agent_engine_id', e.target.value)}
            />
          </InputGroup>
          <InputGroup label="Service Account Path">
            <input 
              type="text"
              className={inputClass}
              placeholder="path/to/service-account.json"
              value={data.memoryCredentials?.service_account_path || ''}
              onChange={(e) => handleCredentialChange('service_account_path', e.target.value)}
            />
            <p className="text-[9px] text-slate-400 mt-0.5">Leave empty to use default credentials</p>
          </InputGroup>
        </div>
      )}

      {/* Credentials section for other cloud providers (non-Vertex AI) */}
      {currentProvider && currentProvider.required_credentials.length > 0 && !currentProvider.auto_configured && data.memoryType !== 'vertex_ai' && (
        <>
          <SectionHeader 
            title="Credentials" 
            expanded={showCredentials} 
            onToggle={() => setShowCredentials(!showCredentials)} 
          />
          {showCredentials && (
            <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3 mt-1">
              {currentProvider.required_credentials.map(cred => (
                <InputGroup key={cred.key} label={cred.label}>
                  {cred.type === 'password' ? (
                    <input 
                      type="password"
                      className={inputClass}
                      placeholder={cred.description}
                      value={data.memoryCredentials?.[cred.key] || ''}
                      onChange={(e) => handleCredentialChange(cred.key, e.target.value)}
                    />
                  ) : cred.type === 'number' ? (
                    <input 
                      type="number"
                      className={inputClass}
                      placeholder={String(cred.default || '')}
                      value={data.memoryCredentials?.[cred.key] || cred.default || ''}
                      onChange={(e) => handleCredentialChange(cred.key, e.target.value)}
                    />
                  ) : (
                    <input 
                      type="text"
                      className={inputClass}
                      placeholder={cred.description}
                      value={data.memoryCredentials?.[cred.key] || ''}
                      onChange={(e) => handleCredentialChange(cred.key, e.target.value)}
                    />
                  )}
                  <p className="text-[9px] text-slate-400 mt-0.5">{cred.description}</p>
                </InputGroup>
              ))}

              {/* Environment variables hint */}
              {currentProvider.env_vars.length > 0 && (
                <div className="text-[9px] text-slate-500 p-2 bg-slate-100 rounded">
                  <strong>Env vars:</strong> {currentProvider.env_vars.join(', ')}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Verify button */}
      <div className="mt-3">
        <button
          onClick={handleVerify}
          disabled={verifying}
          className={`w-full px-3 py-2 rounded text-xs font-semibold transition-all flex items-center justify-center gap-2 ${verifyResult?.success
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
            : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
            }`}
        >
          {verifying ? (
            <>
              <LoadingIcon /> Verifying...
            </>
          ) : verifyResult?.success ? (
            <>
              <CheckIcon className="w-3.5 h-3.5" /> Verified
            </>
          ) : (
            'Verify Configuration'
          )}
        </button>
      </div>

      {/* Verify result */}
      {verifyResult && (
        <div className={`mt-2 p-2 rounded text-[10px] ${verifyResult.success
          ? 'bg-emerald-50 border border-emerald-100 text-emerald-700'
          : 'bg-red-50 border border-red-100 text-red-700'
          }`}>
          {verifyResult.message}
          {verifyResult.details && (
            <div className="mt-1 text-[9px] opacity-75">
              {Object.entries(verifyResult.details).map(([k, v]) => (
                <div key={k}>{k}: {String(v)}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <SectionHeader title="Advanced" expanded={showAdvanced} onToggle={() => setShowAdvanced(!showAdvanced)} />
      {showAdvanced && (
        <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3">
          <InputGroup label="Memory Scope (Optional)">
            <input 
              type="text"
              className={inputClass}
              placeholder="Auto-generated from user context"
              value={data.memoryScope || ''}
              onChange={(e) => onChange({ memoryScope: e.target.value })}
            />
          </InputGroup>
        </div>
      )}

      {data.memoryType === 'vertex_ai' && !memoryStatus?.available && (
        <div className="mt-3 p-2 bg-pink-50 border border-pink-100 rounded text-[10px] text-pink-700">
          <strong>Vertex AI Memory Bank:</strong> Memories are stored per-user in Google Cloud. Requires service account.
        </div>
      )}
    </div>
  );
};

export const AgentEngineNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <span className={labelClass} style={{marginBottom:0}}>Status</span>
      <button 
        onClick={() => onChange({ status: data.status === 'Active' ? 'Inactive' : 'Active' })}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition-colors ${data.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}
      >
          <div className={`w-1.5 h-1.5 rounded-full ${data.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
          {data.status || 'Active'}
      </button>
    </div>

    <StandardAgentFields data={data} onChange={onChange} nodeType="agent_engine" />
    
    <div className="mt-3 p-2 bg-indigo-50 border border-indigo-100 rounded text-[10px] text-indigo-700">
      Connect <strong>Models</strong>, <strong>Memory</strong>, and <strong>Tools</strong> below.
    </div>
  </div>
);

export const LLMNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => {
  const [showCreds, setShowCreds] = useState(false);
  const [showParams, setShowParams] = useState(true);
  const [serviceAccountInfo, setServiceAccountInfo] = useState<{ isValid: boolean; project: string; error?: string } | null>(null);
  const [loadingServiceAccount, setLoadingServiceAccount] = useState(false);

  const selectedProvider = data.modelProvider || '';
  const providerConfig = selectedProvider ? MODEL_OPTIONS[selectedProvider as keyof typeof MODEL_OPTIONS] : null;
  const supportsServiceAccount = providerConfig?.supportsServiceAccount || false;

  // Check service account when provider changes to Vertex AI
  useEffect(() => {
    if (selectedProvider === 'Google Vertex AI' && data.authType === 'service_account') {
      setLoadingServiceAccount(true);
      checkServiceAccount()
        .then(info => {
          setServiceAccountInfo({ isValid: info.isValid, project: info.project || '' });
          if (info.isValid) {
            onChange({ serviceAccountPath: info.path });
          }
        })
        .catch(err => {
          // Network error - assume service account might be configured but backend not reachable
          setServiceAccountInfo({ isValid: false, project: '', error: 'Backend not reachable' });
        })
        .finally(() => setLoadingServiceAccount(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider, data.authType]);

  return (
    <div>
      <InputGroup label="Provider">
        <select 
          className={inputClass}
          value={data.modelProvider || ''}
          onChange={(e) => onChange({ 
            modelProvider: e.target.value, 
            modelName: '',
            authType: e.target.value === 'Google Vertex AI' ? 'service_account' : 'api_key'
          })}
        >
          <option value="" disabled>Select Provider</option>
          {Object.keys(MODEL_OPTIONS).map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </InputGroup>

      {data.modelProvider && providerConfig && (
        <InputGroup label="Model">
          <select 
            className={inputClass}
            value={data.modelName || ''}
            onChange={(e) => onChange({ modelName: e.target.value })}
          >
            <option value="" disabled>Select Model</option>
            {providerConfig.models.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </InputGroup>
      )}

      {/* Authentication Type Selection - Only for providers that support service account */}
      {supportsServiceAccount && (
        <>
          <div className="mt-3 mb-2">
            <label className={labelClass}>Authentication</label>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => onChange({ authType: 'service_account' })}
                className={`flex-1 px-3 py-2 text-xs font-semibold rounded-md border transition-all ${data.authType === 'service_account'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
              >
                🔐 Service Account
              </button>
              <button
                onClick={() => onChange({ authType: 'api_key' })}
                className={`flex-1 px-3 py-2 text-xs font-semibold rounded-md border transition-all ${data.authType === 'api_key'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
              >
                🔑 API Key
              </button>
            </div>
          </div>

          {/* Service Account Status */}
          {data.authType === 'service_account' && (
            <div className={`mt-2 p-2 rounded border text-xs ${loadingServiceAccount
              ? 'bg-slate-50 border-slate-200 text-slate-600'
              : serviceAccountInfo?.isValid
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : serviceAccountInfo?.error
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-amber-50 border-amber-200 text-amber-700'
              }`}>
              {loadingServiceAccount ? (
                <span className="flex items-center gap-2"><LoadingIcon /> Checking service account...</span>
              ) : serviceAccountInfo?.isValid ? (
                <span className="flex items-center gap-2"><CheckIcon /> Service account configured{serviceAccountInfo.project ? ` • Project: ${serviceAccountInfo.project}` : ''}</span>
              ) : serviceAccountInfo?.error ? (
                <span>ℹ️ Service account will be verified on save. Ensure JSON is in backend.</span>
              ) : (
                <span>⚠️ Service account not found. Place service account JSON in backend services folder.</span>
              )}
            </div>
          )}
        </>
      )}

      <SectionHeader title="Credentials" expanded={showCreds} onToggle={() => setShowCreds(!showCreds)} />
      {showCreds && (
        <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3">
          {data.authType === 'api_key' || !supportsServiceAccount ? (
            <InputGroup label="API Key">
              <input 
                type="password" 
                className={inputClass}
                placeholder="sk-..." 
                value={data.apiKey || ''}
                onChange={(e) => onChange({ apiKey: e.target.value })}
              />
            </InputGroup>
          ) : (
            <InputGroup label="Service Account Path (Editable)">
              <input 
                type="text" 
                className={inputClass}
                placeholder="path/to/service-account.json or leave empty for auto-detect" 
                value={data.serviceAccountPath || ''}
                onChange={(e) => onChange({ serviceAccountPath: e.target.value })}
              />
              <p className="text-[9px] text-slate-400 mt-0.5">
                Leave empty to use GOOGLE_APPLICATION_CREDENTIALS from environment
              </p>
            </InputGroup>
          )}
        </div>
      )}

      <SectionHeader title="Parameters" expanded={showParams} onToggle={() => setShowParams(!showParams)} />
      {showParams && (
        <div className="space-y-4 pt-2">
          <div>
            <div className="flex justify-between text-[10px] text-slate-500 mb-1">
              <span>Temperature</span>
              <span className="font-mono">{data.temperature ?? 1}</span>
            </div>
            <input 
              type="range" min="0" max="2" step="0.1" 
              className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
              value={data.temperature ?? 1}
              onChange={(e) => onChange({ temperature: parseFloat(e.target.value) })}
            />
          </div>
          <div>
            <div className="flex justify-between text-[10px] text-slate-500 mb-1">
              <span>Top P</span>
              <span className="font-mono">{data.topP ?? 0.95}</span>
            </div>
            <input 
              type="range" min="0" max="1" step="0.05" 
              className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
              value={data.topP ?? 0.95}
              onChange={(e) => onChange({ topP: parseFloat(e.target.value) })}
            />
          </div>
          <InputGroup label="Max Output Tokens">
            <input 
              type="number" 
              className={inputClass}
              placeholder="8192" 
              value={data.maxOutputTokens || ''}
              onChange={(e) => onChange({ maxOutputTokens: parseInt(e.target.value) || undefined })}
            />
          </InputGroup>
        </div>
      )}
    </div>
  );
};

export const MCPNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => {
  const [availableTools, setAvailableTools] = useState<MCPTool[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [connectionError, setConnectionError] = useState('');

  const selectedTools = data.selectedMcpTools || [];

  const connectToServer = async (url: string) => {
    if (!url) return;
    setLoading(true);
    setConnectionStatus('connecting');
    setConnectionError('');
    try {
      const tools = await fetchMCPTools(url);

      // Filter tools based on activated tools from admin panel
      const activatedToolsStr = localStorage.getItem('activatedMCPTools');
      let filteredByActivation = tools;
      if (activatedToolsStr) {
        try {
          const activatedTools = JSON.parse(activatedToolsStr);
          filteredByActivation = tools.filter(tool => activatedTools.includes(tool.name));
        } catch (e) {
          console.error('Error filtering activated tools:', e);
        }
      }

      setAvailableTools(filteredByActivation);
      setConnectionStatus('connected');
    } catch (err: any) {
      setConnectionStatus('error');
      setConnectionError(err.message || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  };

  // Fetch MCP tools on mount if URL exists
  useEffect(() => {
    if (data.mcpServerUrl) {
      connectToServer(data.mcpServerUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTools = useMemo(() => {
    return availableTools.filter(tool => 
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableTools, searchTerm]);

  const toggleTool = (toolName: string) => {
    const current = selectedTools || [];
    if (current.includes(toolName)) {
      onChange({ selectedMcpTools: current.filter(t => t !== toolName) });
    } else {
      onChange({ selectedMcpTools: [...current, toolName] });
    }
  };

  const selectAllFiltered = () => {
    const toolNames = filteredTools.map(t => t.name);
    const current = selectedTools || [];
    const newSelection = Array.from(new Set([...current, ...toolNames]));
    onChange({ selectedMcpTools: newSelection });
  };

  const clearSelection = () => {
    onChange({ selectedMcpTools: [] });
  };

  // Legacy manual tools
  const tools = data.tools || [];

  const addManualTool = () => {
    const newTool = { 
      id: Math.random().toString(36).substr(2, 9), 
      name: '', endpoint: '', method: 'GET'
    };
    onChange({ tools: [...tools, newTool] });
  };

  const updateManualTool = (id: string, field: string, value: string) => {
    onChange({ tools: tools.map(t => t.id === id ? { ...t, [field]: value } : t) });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <InputGroup label="MCP Server URL">
            <input
              type="text"
              className={inputClass}
              placeholder={MCP_BASE}
              value={data.mcpServerUrl || ''}
              onChange={(e) => {
                onChange({ mcpServerUrl: e.target.value });
                setConnectionStatus('idle');
              }}
            />
          </InputGroup>
        </div>
        <button
          onClick={() => connectToServer(data.mcpServerUrl || MCP_BASE)}
          disabled={loading}
          className={`mb-[1.5px] px-3 py-2 text-xs font-bold rounded-md transition-all border ${connectionStatus === 'connected'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
            } disabled:opacity-50`}
        >
          {loading ? '...' : connectionStatus === 'connected' ? 'Connected' : 'Connect'}
        </button>
      </div>

      {/* Connection Status Indicator */}
      {connectionStatus !== 'idle' && (
        <div className={`text-[10px] px-2 py-1.5 rounded flex items-center gap-1.5 font-medium ${connectionStatus === 'connecting' ? 'bg-slate-50 text-slate-500 border border-slate-100' :
          connectionStatus === 'connected' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
            'bg-red-50 text-red-700 border border-red-100'
          }`}>
          {connectionStatus === 'connecting' && <LoadingIcon />}
          {connectionStatus === 'connected' && <CheckIcon className="w-3 h-3" />}
          {connectionStatus === 'error' && <span className="text-red-500">❌</span>}
          <span>
            {connectionStatus === 'connecting' && 'Connecting to MCP Server...'}
            {connectionStatus === 'connected' && `Successfully connected. ${availableTools.length} tools available.`}
            {connectionStatus === 'error' && `Connection failed: ${connectionError}`}
          </span>
        </div>
      )}

      {/* Search and Selection Controls */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input 
            type="text"
            className="w-full text-xs pl-8 pr-3 py-2 border border-slate-200 rounded-md focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
            placeholder="Search tools..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute left-2.5 top-2.5 text-slate-400">
            <SearchIcon />
          </div>
        </div>
        <button 
          onClick={selectAllFiltered}
          className="px-2 py-1.5 text-[10px] font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 transition-colors"
        >
          Select All
        </button>
      </div>

      {/* Selected Count */}
      {selectedTools.length > 0 && (
        <div className="flex items-center justify-between bg-orange-50 px-2 py-1.5 rounded border border-orange-100">
          <span className="text-xs text-orange-700 font-medium">{selectedTools.length} tool(s) selected</span>
          <button 
            onClick={clearSelection}
            className="text-[10px] text-orange-600 hover:text-orange-800 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Tools List */}
      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 text-xs">
          <LoadingIcon /> <span className="ml-2">Loading tools...</span>
        </div>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
          {filteredTools.map(tool => {
            const isSelected = selectedTools.includes(tool.name);
            return (
              <div 
                key={tool.name}
                onClick={() => toggleTool(tool.name)}
                className={`p-2 rounded border cursor-pointer flex items-center justify-between group transition-all ${isSelected
                  ? 'bg-orange-50 border-orange-300'
                  : 'bg-white border-slate-100 hover:border-orange-200 hover:bg-orange-50/50'
                  }`}
              >
                <div className="flex flex-col flex-1 min-w-0">
                  <span className={`text-xs font-medium truncate ${isSelected ? 'text-orange-700' : 'text-slate-700'}`}>
                    {tool.name}
                  </span>
                  <span className="text-[9px] text-slate-400 truncate">{tool.description}</span>
                </div>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-300 group-hover:border-orange-300'
                  }`}>
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                </div>
              </div>
            );
          })}
          {filteredTools.length === 0 && !loading && (
            <div className="text-center py-4 text-[10px] text-slate-400">
              No tools found. {searchTerm && 'Try a different search term.'}
            </div>
          )}
        </div>
      )}

      {/* Manual Tool Addition (Collapsed) */}
      <SectionHeader title="Add Custom Tools" expanded={showManual} onToggle={() => setShowManual(!showManual)} />
      {showManual && (
        <div className="space-y-2">
          {tools.map((tool) => (
            <div key={tool.id} className="p-3 bg-white border border-slate-200 rounded-md shadow-sm relative group hover:border-orange-300 transition-colors">
              <button 
                onClick={() => onChange({ tools: tools.filter(t => t.id !== tool.id) })}
                className="absolute top-2 right-2 text-slate-300 hover:text-red-500 transition-colors"
              >
                <TrashIcon />
              </button>
              
              <div className="space-y-2">
                <input 
                  className="w-full text-sm font-bold border-none p-0 focus:ring-0 text-slate-800 placeholder-slate-300" 
                  placeholder="Tool Name"
                  value={tool.name}
                  onChange={(e) => updateManualTool(tool.id, 'name', e.target.value)}
                />
                <div className="flex gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono text-slate-500">{tool.method}</span>
                  <input 
                    className="flex-1 text-xs border-b border-slate-100 focus:border-orange-400 outline-none pb-0.5" 
                    placeholder="http://api..."
                    value={tool.endpoint}
                    onChange={(e) => updateManualTool(tool.id, 'endpoint', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
          <button 
            onClick={addManualTool}
            className="w-full py-2 border border-dashed border-slate-300 rounded text-slate-500 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 text-xs font-semibold transition-all"
          >
            + Add Custom Tool
          </button>
        </div>
      )}
    </div>
  );
};

export const IntegrationNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [dynamicAgents, setDynamicAgents] = useState<ToolAgentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetch agents from backend on mount
  useEffect(() => {
    setLoading(true);
    fetchToolAgents()
      .then(agents => {
        if (agents.length > 0) {
          setDynamicAgents(agents);
        }
        setLoadError(null);
      })
      .catch(err => {
        console.error('Failed to fetch tool agents:', err);
        setLoadError('Could not load agents from server');
      })
      .finally(() => setLoading(false));
  }, []);

  // Combine dynamic agents with local registry (fallback)
  const allAgents: ToolAgentInfo[] = useMemo(() => {
    if (dynamicAgents.length > 0) {
      return dynamicAgents;
    }
    // Fallback to local registry
    return TOOL_AGENTS_REGISTRY.map(a => ({
      id: a.id,
      name: a.name,
      description: a.description,
      instruction: a.systemInstruction,
      model: 'gemini-2.5-flash',
      has_tools: true,
      source: 'builtin' as const,
      config_fields: [],
      has_required_config: false
    }));
  }, [dynamicAgents]);

  const filteredAgents = useMemo(() => {
    return allAgents.filter(agent => 
      agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allAgents, searchTerm]);

  // Find selected agent from either source
  const selectedAgentDef: ToolAgentInfo | null = useMemo(() => {
    // First try dynamic agents
    const dynamic = dynamicAgents.find(a => a.id === data.selectedToolAgent);
    if (dynamic) return dynamic;
    // Fallback to local registry
    const local = TOOL_AGENTS_REGISTRY.find(a => a.id === data.selectedToolAgent);
    if (local) {
      return {
        id: local.id,
        name: local.name,
        description: local.description,
        instruction: local.systemInstruction,
        model: 'gemini-2.5-flash',
        has_tools: true,
        source: 'builtin' as const,
        config_fields: [],
        has_required_config: false
      };
    }
    return null;
  }, [data.selectedToolAgent, dynamicAgents]);

  // Get fields from local registry for config panel
  const localAgentFields = useMemo(() => {
    const local = TOOL_AGENTS_REGISTRY.find(a => a.id === data.selectedToolAgent);
    return local?.fields || [];
  }, [data.selectedToolAgent]);

  // Get config fields from backend-fetched agent (dynamic agents)
  const dynamicConfigFields = useMemo(() => {
    const dynamic = dynamicAgents.find(a => a.id === data.selectedToolAgent);
    return dynamic?.config_fields || [];
  }, [data.selectedToolAgent, dynamicAgents]);

  // Combine: prefer dynamic config fields, fallback to local
  const configFields = useMemo(() => {
    if (dynamicConfigFields.length > 0) return dynamicConfigFields;
    // Convert local fields to ToolAgentConfigField format
    return localAgentFields.map(f => ({
      key: f.name,
      label: f.label,
      type: f.type || 'text',
      required: f.required || false,
      default: '',
      description: '',
      options: f.options
    }));
  }, [dynamicConfigFields, localAgentFields]);

  const selectAgent = (agent: ToolAgentInfo) => {
    onChange({
      selectedToolAgent: agent.id,
      agentName: agent.name,
      description: agent.description,
      systemPrompt: agent.instruction,
      toolParams: {}
    });
  };

  const handleParamChange = (field: string, value: any) => {
    onChange({
      toolParams: {
        ...data.toolParams,
        [field]: value
      }
    });
  };

  if (selectedAgentDef) {
    return (
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
           <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-700">Marketplace Agent</span>
              {selectedAgentDef.has_tools && (
                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[9px] font-bold rounded">
                  HAS TOOLS
                </span>
              )}
           </div>
          <button 
            onClick={() => onChange({ selectedToolAgent: undefined, agentName: '', description: '', systemPrompt: '', toolParams: {} })}
            className="text-[10px] text-blue-500 hover:text-blue-700 hover:underline"
          >
            Change Agent
          </button>
        </div>
        
        <StandardAgentFields data={data} onChange={onChange} nodeType="integration" />
        
        {/* Configuration section - from backend config_fields or local registry */}
        {configFields.length > 0 && (
          <>
            <SectionHeader 
              title={`Configuration ${selectedAgentDef.has_required_config ? '(Required)' : ''}`} 
              expanded={showConfig} 
              onToggle={() => setShowConfig(!showConfig)} 
            />
            {showConfig && (
              <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3 mt-1">
                {configFields.map(field => (
                  <InputGroup key={field.key} label={`${field.label} ${field.required ? '*' : '(Optional)'}`}>
                    {field.type === 'select' ? (
                      <select
                        className={inputClass}
                        value={data.toolParams?.[field.key] || field.default || ''}
                        onChange={(e) => handleParamChange(field.key, e.target.value)}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        className={inputClass}
                        rows={3}
                        placeholder={field.description || ''}
                        value={data.toolParams?.[field.key] || field.default || ''}
                        onChange={(e) => handleParamChange(field.key, e.target.value)}
                      />
                    ) : field.type === 'email' ? (
                      <input
                        type="email"
                        className={inputClass}
                        placeholder={field.description || 'email@example.com'}
                        value={data.toolParams?.[field.key] || field.default || ''}
                        onChange={(e) => handleParamChange(field.key, e.target.value)}
                      />
                    ) : field.type === 'number' ? (
                      <input
                        type="number"
                        className={inputClass}
                        placeholder={field.description || ''}
                        value={data.toolParams?.[field.key] || field.default || ''}
                        onChange={(e) => handleParamChange(field.key, e.target.value)}
                      />
                    ) : field.type === 'password' ? (
                      <input
                        type="password"
                        className={inputClass}
                        placeholder={field.description || ''}
                        value={data.toolParams?.[field.key] || ''}
                        onChange={(e) => handleParamChange(field.key, e.target.value)}
                      />
                    ) : (
                      <input
                        type="text"
                        className={inputClass}
                        placeholder={field.description || ''}
                        value={data.toolParams?.[field.key] || field.default || ''}
                        onChange={(e) => handleParamChange(field.key, e.target.value)}
                      />
                    )}
                    {field.description && (
                      <p className="text-[9px] text-slate-400 mt-0.5">{field.description}</p>
                    )}
                  </InputGroup>
                ))}
              </div>
            )}
          </>
        )}

        {/* Legacy local fields - only shown if no configFields */}
        {configFields.length === 0 && localAgentFields.length > 0 && (
          <>
            <SectionHeader title="Configuration" expanded={showConfig} onToggle={() => setShowConfig(!showConfig)} />
            {showConfig && (
              <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3 mt-1">
                {localAgentFields.map(field => (
                  <InputGroup key={field.name} label={`${field.label} ${field.required ? '*' : ''}`}>
                    {field.type === 'select' ? (
                      <select
                        className={inputClass}
                        value={data.toolParams?.[field.name] || ''}
                        onChange={(e) => handleParamChange(field.name, e.target.value)}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        className={inputClass}
                        rows={3}
                        value={data.toolParams?.[field.name] || ''}
                        onChange={(e) => handleParamChange(field.name, e.target.value)}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        className={inputClass}
                        value={data.toolParams?.[field.name] || ''}
                        onChange={(e) => handleParamChange(field.name, e.target.value)}
                      />
                    )}
                  </InputGroup>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="relative mb-3">
        <input 
          type="text"
          className="w-full text-xs pl-8 pr-3 py-2 border border-slate-200 rounded-md focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
          placeholder="Search marketplace..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="absolute left-2.5 top-2.5 text-slate-400">
          <SearchIcon />
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 text-xs">
          <LoadingIcon /> <span className="ml-2">Loading agents...</span>
        </div>
      ) : loadError ? (
        <div className="text-center py-4 text-[10px] text-amber-600 bg-amber-50 rounded p-2">
          {loadError}. Using local agents.
        </div>
      ) : null}

      {/* Agent count */}
      {!loading && (
        <div className="text-[10px] text-slate-500 mb-2">
          {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''} available
          {dynamicAgents.length > 0 && <span className="text-emerald-600 ml-1">(live)</span>}
        </div>
      )}

      <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
        {filteredAgents.map(agent => (
          <div 
            key={agent.id}
            onClick={() => selectAgent(agent)}
            className="p-2 rounded border border-slate-100 bg-white hover:border-purple-300 hover:bg-purple-50 cursor-pointer flex items-center justify-between group transition-all"
          >
            <div className="flex flex-col">
                 <div className="flex items-center gap-1.5">
                   <span className="text-xs font-medium text-slate-700 group-hover:text-purple-700">{agent.name}</span>
                   {agent.source === 'generated' && (
                     <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] font-bold rounded">
                       USER
                     </span>
                   )}
                   {agent.has_required_config && (
                     <span className="px-1 py-0.5 bg-amber-100 text-amber-700 text-[8px] font-bold rounded">
                       CONFIG
                     </span>
                   )}
                 </div>
                 <span className="text-[9px] text-slate-400 truncate w-32">{agent.description}</span>
            </div>
            <span className="opacity-0 group-hover:opacity-100 text-purple-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </span>
          </div>
        ))}
        {filteredAgents.length === 0 && (
          <div className="text-center py-4 text-[10px] text-slate-400">No agents found</div>
        )}
      </div>
    </div>
  );
};
// --- EVALUATION NODE ---
export const EvaluationNode = ({ data, onChange, nodeId, nodes, connections }: { data: NodeData, onChange: (d: Partial<NodeData>) => void, nodeId?: string, nodes?: WorkflowNode[], connections?: Connection[] }) => {
  const [showMetrics, setShowMetrics] = useState(true);
  const [showThresholds, setShowThresholds] = useState(false);
  const [showGroundedness, setShowGroundedness] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testFile, setTestFile] = useState<{ name: string; type: string; base64: string } | null>(null);
  const [isFullPage, setIsFullPage] = useState(false);
  const [deployedAgents, setDeployedAgents] = useState<string[]>([]);

  const [testMode, setTestMode] = useState<'agent' | 'workflow'>(data.evaluationConfig?.testMode || 'agent');

  // Collect all agent_engine nodes in the workflow for display
  const workflowAgents = useMemo(() => {
    if (!nodes) return [];
    return nodes.filter(n => n.type === 'agent_engine' && n.data.agentName).map(n => n.data.agentName as string);
  }, [nodes]);

  // Root workflow agent = first agent_engine node (the orchestrator, same as main chat UI)
  const workflowRootAgent = useMemo(() => {
    if (!nodes) return null;
    const root = nodes.find(n => n.type === 'agent_engine' && n.data.agentName);
    return root?.data.agentName as string || null;
  }, [nodes]);

  // Auto-detect connected agent from workflow graph (traverse edges backward)
  const connectedAgent = useMemo(() => {
    if (!nodeId || !nodes || !connections) return null;
    // BFS backward from this evaluation node to find the nearest agent_engine
    const visited = new Set<string>();
    const queue = [nodeId];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);
      // Find all edges pointing TO this node
      const inbound = connections.filter(c => c.target === current);
      for (const edge of inbound) {
        const sourceNode = nodes.find(n => n.id === edge.source);
        if (sourceNode?.type === 'agent_engine' && sourceNode.data.agentName) {
          return sourceNode.data.agentName;
        }
        queue.push(edge.source);
      }
    }
    return null;
  }, [nodeId, nodes, connections]);

  // Fetch deployed agents for target agent selector
  useEffect(() => {
    fetch(apiUrl('/agent-builder/tool-agents'))
      .then(r => r.json())
      .then((agents: Array<{id: string; name: string; source: string}>) => {
        const names = agents
          .filter(a => !['evaluation_demo_agents', 'base_agent'].includes(a.id))
          .map(a => a.name);
        setDeployedAgents(names);
      })
      .catch(() => setDeployedAgents([]));
  }, []);

  // Initialize evaluation config if not set
  useEffect(() => {
    if (!data.evaluationConfig) {
      onChange({
        agentName: 'Evaluation',
        description: 'Hybrid-EvA: Expert AI auditor for dual-mode evaluation of agents',
        systemPrompt: EVALUATION_SYSTEM_INSTRUCTION,
        evaluationConfig: {
          mode: 'hybrid',
          passThreshold: 85,
          warnThreshold: 70,
          metrics: DEFAULT_EVALUATION_METRICS,
          onFail: {
            blockResponse: false,
            logIncident: true,
            notifyAdmin: true,
            attachWarningBanner: true
          }
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const config = data.evaluationConfig || {
    mode: 'hybrid' as const,
    passThreshold: 85,
    warnThreshold: 70,
    metrics: DEFAULT_EVALUATION_METRICS,
    onFail: { blockResponse: false, logIncident: true, notifyAdmin: true, attachWarningBanner: true }
  };

  const enabledMetrics = config.metrics.filter(m => m.enabled);
  const totalWeight = enabledMetrics.reduce((sum, m) => sum + m.weight, 0);

  const updateConfig = (partial: Partial<EvaluationConfig>) => {
    onChange({
      evaluationConfig: { ...config, ...partial }
    });
  };

  const updateMetric = (metricId: string, field: string, value: any) => {
    if (field === 'weight') {
      // Smart weight management: freely adjust, but cap so total never exceeds 100
      const newWeight = value as number;
      const otherEnabledTotal = config.metrics
        .filter(m => m.id !== metricId && m.enabled)
        .reduce((s, m) => s + m.weight, 0);
      // Cap this metric so total stays ≤ 100
      const maxAllowed = 100 - otherEnabledTotal;
      const clampedWeight = Math.max(0, Math.min(newWeight, maxAllowed));
      const updatedMetrics = config.metrics.map(m =>
        m.id === metricId ? { ...m, weight: clampedWeight } : m
      );
      updateConfig({ metrics: updatedMetrics });
    } else if (field === 'enabled') {
      // When toggling a metric, redistribute weights equally among all enabled metrics
      const willBeEnabled = value as boolean;
      const updatedMetrics = config.metrics.map(m => m.id === metricId ? { ...m, enabled: willBeEnabled } : m);
      const nowEnabled = updatedMetrics.filter(m => m.enabled);
      if (nowEnabled.length > 0) {
        const equalWeight = Math.floor(100 / nowEnabled.length);
        const remainder = 100 - (equalWeight * nowEnabled.length);
        let idx = 0;
        const balanced = updatedMetrics.map(m => {
          if (!m.enabled) return { ...m, weight: 0 };
          const w = equalWeight + (idx < remainder ? 1 : 0);
          idx++;
          return { ...m, weight: w };
        });
        updateConfig({ metrics: balanced });
      } else {
        updateConfig({ metrics: updatedMetrics.map(m => ({ ...m, weight: 0 })) });
      }
    } else {
      const updatedMetrics = config.metrics.map(m => m.id === metricId ? { ...m, [field]: value } : m);
      updateConfig({ metrics: updatedMetrics });
    }
  };

  const normalizeWeights = () => {
    const enabledMetricsCount = enabledMetrics.length;
    if (enabledMetricsCount === 0) return;
    
    const equalWeight = Math.floor(100 / enabledMetricsCount);
    const remainder = 100 - (equalWeight * enabledMetricsCount);
    
    const updatedMetrics = config.metrics.map((m, idx) => {
      if (!m.enabled) return m;
      const extraWeight = idx < remainder ? 1 : 0;
      return { ...m, weight: equalWeight + extraWeight };
    });
    
    updateConfig({ metrics: updatedMetrics });
  };

  // Run evaluation test
  const runEvaluationTest = async () => {
    if (!data.testInput?.trim() && !testFile) {
      onChange({ testOutput: '⚠️ Please enter a test query in the input field above.\nExample: What is the capital of France?' });
      return;
    }

    setTestLoading(true);
    const userEmail = (() => { try { const u = JSON.parse(localStorage.getItem('user_data') || '{}'); return u.email || 'user'; } catch { return 'user'; } })();

    // Resolve which agent to test:
    // - Workflow mode → root orchestrator agent (e.g. Wealth_Manager) — same as main chat UI
    // - Agent mode → manually selected OR auto-detected connected agent
    const resolvedAgent = testMode === 'workflow'
      ? (workflowRootAgent || connectedAgent || '')
      : (config.targetAgent || connectedAgent || '');

    const modeLabel = testMode === 'workflow'
      ? `Running full workflow via ${resolvedAgent || 'root agent'}...`
      : 'Running evaluation...';

    onChange({ testOutput: modeLabel });

    try {
      const response = await fetch(apiUrl('/agent-builder/node/test'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_type: 'evaluation',
          node_data: {
            evaluationConfig: {
              ...config,
              targetAgent: resolvedAgent
            },
            systemPrompt: data.systemPrompt || EVALUATION_SYSTEM_INSTRUCTION
          },
          input: data.testInput || '',
          file_data: testFile || undefined,
          user_email: userEmail
        })
      });

      const result = await response.json();
      if (result.success) {
        onChange({
          testOutput: result.output,
          evaluationResults: result.evaluation_results
        });
      } else {
        onChange({ testOutput: `❌ Error: ${result.error || 'Evaluation failed'}` });
      }
    } catch (error) {
      onChange({ testOutput: `❌ Error: ${error}` });
    } finally {
      setTestLoading(false);
    }
  };

  // Full page modal wrapper - use Portal to render at document root
  const fullPageModal = isFullPage ? createPortal(
    <div 
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
      onClick={() => setIsFullPage(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
              <span className="text-xl">⚡</span>
            </div>
            <div>
              <h2 className="text-lg font-bold">Evaluation Node</h2>
              <p className="text-xs text-slate-400">Hybrid-EvA: Expert AI Auditor</p>
            </div>
          </div>
          <button
            onClick={() => setIsFullPage(false)}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
            </svg>
            Collapse
          </button>
        </div>

        {/* Content - Scrollable - Two Column Layout */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Configuration */}
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-4">Configuration</h3>

                {/* Mode Selection */}
                <div className="mb-4">
                  <label className="text-xs font-semibold text-slate-600 mb-2 block">Evaluation Mode</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateConfig({ mode: 'llm_judge' })}
                      className={`flex-1 px-4 py-3 text-sm font-semibold rounded-lg border-2 transition-all ${config.mode === 'llm_judge'
                        ? 'bg-sky-50 border-sky-400 text-sky-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                    >
                      🧠 LLM-as-Judge
                    </button>
                    <button
                      onClick={() => updateConfig({ mode: 'hybrid' })}
                      className={`flex-1 px-4 py-3 text-sm font-semibold rounded-lg border-2 transition-all ${config.mode === 'hybrid'
                        ? 'bg-sky-50 border-sky-400 text-sky-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                    >
                      🔀 Hybrid
                    </button>
                  </div>
                </div>

                    {/* Thresholds */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1 block">PASS Threshold</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0" max="100"
                            value={config.passThreshold}
                            onChange={(e) => updateConfig({ passThreshold: parseInt(e.target.value) || 85 })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1 block">WARN Threshold</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0" max="100"
                            value={config.warnThreshold}
                            onChange={(e) => updateConfig({ warnThreshold: parseInt(e.target.value) || 70 })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                      </div>
                    </div>

                    {/* Ground Truth */}
                    {config.mode === 'hybrid' && (
                      <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1 block">Ground Truth (Optional)</label>
                        <textarea
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
                          rows={3}
                          placeholder="Enter expected answer for comparison..."
                          value={config.groundTruth || ''}
                          onChange={(e) => updateConfig({ groundTruth: e.target.value })}
                        />
                      </div>
                    )}

                    {/* Judge Model */}
                    <div className="mt-4">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Judge Model</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                        value={config.judgeModel || 'gemini-3-pro-preview'}
                        onChange={(e) => updateConfig({ judgeModel: e.target.value })}
                      >
                        <option value="gemini-3-pro-preview">Gemini 3 Pro (Default)</option>
                        <option value="gemini-2.5-flash-preview-05-20">Gemini 2.5 Flash</option>
                        <option value="gemini-2.5-pro-preview-05-06">Gemini 2.5 Pro</option>
                        <option value="gemini-2.0-flash">Gemini 2.0 Flash (Fastest)</option>
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">Model used by the AI judge for quality evaluation</p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-slate-800">Metrics</h3>
                      <span className={`text-xs font-bold ${totalWeight === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {totalWeight} / 100
                      </span>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {config.metrics.map(metric => (
                        <div key={metric.id} className={`p-3 rounded-lg border ${metric.enabled ? 'bg-white border-slate-200' : 'bg-slate-100 border-slate-100'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={metric.enabled}
                                onChange={(e) => updateMetric(metric.id, 'enabled', e.target.checked)}
                                className="w-4 h-4 rounded"
                              />
                              <span className={`text-sm font-medium ${metric.enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                                {metric.name}
                              </span>
                            </div>
                            <span className={`text-sm font-bold ${metric.enabled ? 'text-sky-600' : 'text-slate-300'}`}>
                              {metric.weight}%
                            </span>
                          </div>
                          {metric.enabled && (
                            <input
                              type="range" min="0" max="100"
                              value={metric.weight}
                              onChange={(e) => updateMetric(metric.id, 'weight', parseInt(e.target.value))}
                              className="w-full mt-2 h-2 bg-slate-200 rounded-lg accent-sky-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Testing */}
                <div className="space-y-6">
                  <div className="bg-sky-50 rounded-xl p-4 border border-sky-100">
                    <h3 className="text-sm font-bold text-sky-800 mb-4">🧪 Test Evaluation</h3>
                    
                    {/* Test Mode Toggle */}
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Test Mode</label>
                      <div className="flex gap-1 p-1 bg-white rounded-lg border border-slate-200">
                        <button
                          onClick={() => setTestMode('agent')}
                          className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${testMode === 'agent' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                        >
                          🤖 Single Agent
                        </button>
                        <button
                          onClick={() => setTestMode('workflow')}
                          className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${testMode === 'workflow' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                        >
                          🔄 Full Workflow
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {testMode === 'agent' 
                          ? 'Tests a single agent and evaluates its response' 
                          : 'Runs the full workflow pipeline, then evaluates the final output'}
                      </p>
                    </div>

                    {/* Target Agent - only show for single agent mode */}
                    {testMode === 'agent' && (
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Target Agent</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                        value={config.targetAgent || ''}
                        onChange={(e) => updateConfig({ targetAgent: e.target.value })}
                      >
                        <option value="">{connectedAgent ? `Auto — ${connectedAgent} (from workflow)` : 'Auto (first deployed agent)'}</option>
                        {deployedAgents.map(a => (
                          <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                      {connectedAgent && !config.targetAgent && (
                        <p className="text-[10px] text-emerald-600 mt-1">✓ Auto-detected from connected workflow node</p>
                      )}
                    </div>
                    )}

                    {/* Workflow agents summary - only for workflow mode */}
                    {testMode === 'workflow' && (
                      <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                        {workflowRootAgent && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-bold text-indigo-800">Root Agent:</span>
                            <span className="text-[10px] px-2 py-0.5 bg-indigo-600 text-white rounded-full font-bold">{workflowRootAgent}</span>
                          </div>
                        )}
                        <p className="text-[10px] text-indigo-600">Runs the query through the root orchestrator — same as the main chat UI. The root agent delegates to sub-agents automatically.</p>
                        {workflowAgents.length > 1 && (
                          <div className="mt-2">
                            <p className="text-[9px] text-indigo-500 mb-1">Sub-agents ({workflowAgents.length - 1}):</p>
                            <div className="flex flex-wrap gap-1">
                              {workflowAgents.filter(a => a !== workflowRootAgent).map(a => (
                                <span key={a} className="text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded-full">{a}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="text-xs font-semibold text-slate-600 mb-1 block">Test Input</label>
                      <textarea 
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                        rows={5}
                        placeholder='Type your query here like a normal chat message...'
                        value={data.testInput || ''}
                        onChange={(e) => onChange({ testInput: e.target.value })}
                      />
                    </div>

                    <button
                      onClick={runEvaluationTest}
                      disabled={testLoading}
                      className={`w-full px-4 py-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        testLoading 
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                          : testMode === 'workflow' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-sky-600 text-white hover:bg-sky-700'
                      }`}
                    >
                      {testLoading ? (
                        <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span> Running...</>
                      ) : testMode === 'workflow' ? (
                        <>🔄 Run Workflow + Evaluate</>
                      ) : (
                        <>🚀 Run Evaluation</>
                      )}
                    </button>

                    {/* Output */}
                    {data.testOutput && (
                      <div className="mt-4">
                        <label className="text-xs font-semibold text-slate-600 mb-1 block">Results</label>
                        <pre className="w-full p-3 bg-white border border-slate-200 rounded-lg text-xs font-mono whitespace-pre-wrap max-h-80 overflow-y-auto">
                          {data.testOutput}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
    document.body
  ) : null;

  return (
    <>
      {fullPageModal}
      <div className="animate-fade-in">
      {/* Full Page Toggle Button */}
      <div className="flex items-center justify-between mb-4">
        <span className={labelClass} style={{marginBottom:0}}>Status</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFullPage(true)}
            className="px-2 py-1 text-[10px] font-medium text-sky-600 bg-sky-50 border border-sky-200 rounded hover:bg-sky-100 transition-colors flex items-center gap-1"
            title="Open in full page"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
            Expand
          </button>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
            Active
          </div>
        </div>
      </div>

      {/* Static Agent Info */}
      <InputGroup label="Agent Name">
        <input 
          type="text" 
          className={`${inputClass} bg-slate-50`}
          value="Evaluation"
          disabled
        />
      </InputGroup>
      
      <InputGroup label="Description">
        <textarea 
          className={`${inputClass} resize-none bg-slate-50 text-slate-600`}
          rows={2}
          value="Hybrid-EvA: Expert AI auditor for dual-mode evaluation"
          disabled
        />
      </InputGroup>

      <InputGroup label="System Instruction">
        <textarea 
          className={`${inputClass} font-mono text-[10px] bg-slate-50 text-slate-600`}
          rows={4}
          value={data.systemPrompt || EVALUATION_SYSTEM_INSTRUCTION}
          disabled
        />
      </InputGroup>

        {/* Evaluation Mode Selection */}
        <div className="border-t border-slate-100 my-4"></div>
        <div className="mb-4">
          <label className={labelClass}>Evaluation Mode</label>
          <div className="flex gap-2 mt-1.5">
            <button
              onClick={() => updateConfig({ mode: 'llm_judge' })}
              className={`flex-1 px-3 py-2 text-xs font-semibold rounded-md border transition-all ${config.mode === 'llm_judge'
                ? 'bg-sky-50 border-sky-300 text-sky-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
            >
              🧠 LLM-as-Judge
            </button>
            <button
              onClick={() => updateConfig({ mode: 'hybrid' })}
              className={`flex-1 px-3 py-2 text-xs font-semibold rounded-md border transition-all ${config.mode === 'hybrid'
                ? 'bg-sky-50 border-sky-300 text-sky-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            🔀 Hybrid
          </button>
        </div>
        <p className="text-[9px] text-slate-400 mt-1">
          {config.mode === 'llm_judge' 
            ? 'Reference-free evaluation using AI judgment only' 
            : 'Combines AI judgment with ground truth / pattern matching'}
        </p>
      </div>

      {/* Metrics Section */}
      <SectionHeader title="Metric Selection" expanded={showMetrics} onToggle={() => setShowMetrics(!showMetrics)} />
      {showMetrics && (
        <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3 mt-1">
          {/* Weights Summary */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-slate-600">Weights</span>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold ${totalWeight === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {totalWeight} / 100
              </span>
              <button
                onClick={normalizeWeights}
                className="text-[9px] text-sky-600 hover:underline"
              >
                Normalize
              </button>
            </div>
          </div>
          
          {/* Weight Progress Bar */}
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
            <div 
              className={`h-full transition-all ${totalWeight === 100 ? 'bg-emerald-500' : totalWeight > 100 ? 'bg-red-500' : 'bg-amber-500'}`}
              style={{ width: `${Math.min(totalWeight, 100)}%` }}
            />
          </div>

          {/* Metrics List */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {config.metrics.map(metric => (
              <div key={metric.id} className={`p-2 rounded border ${metric.enabled ? 'bg-white border-slate-200' : 'bg-slate-100 border-slate-100'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={metric.enabled}
                      onChange={(e) => updateMetric(metric.id, 'enabled', e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span className={`text-[10px] font-semibold ${metric.enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                      {metric.name}
                    </span>
                    {metric.required && (
                      <span className="px-1 py-0.5 bg-red-100 text-red-600 text-[8px] font-bold rounded">Required</span>
                    )}
                  </div>
                  <span className={`text-[10px] font-bold ${metric.enabled ? 'text-sky-600' : 'text-slate-300'}`}>
                    {metric.weight}
                  </span>
                </div>
                <p className="text-[9px] text-slate-400 ml-5 mb-1">{metric.description}</p>
                {metric.enabled && (
                  <div className="ml-5 mt-1">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={metric.weight}
                      onChange={(e) => updateMetric(metric.id, 'weight', parseInt(e.target.value))}
                      className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-500"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Thresholds & Actions Section */}
      <SectionHeader title="Thresholds & Actions" expanded={showThresholds} onToggle={() => setShowThresholds(!showThresholds)} />
      {showThresholds && (
        <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-4 mt-1">
          {/* Thresholds */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>PASS ≥</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.passThreshold}
                onChange={(e) => updateConfig({ passThreshold: parseInt(e.target.value) || 85 })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>WARN ≥</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.warnThreshold}
                onChange={(e) => updateConfig({ warnThreshold: parseInt(e.target.value) || 70 })}
                className={inputClass}
              />
              <p className="text-[9px] text-slate-400 mt-0.5">Below this → FAIL</p>
            </div>
          </div>

          <p className="text-[9px] text-slate-500 bg-slate-100 p-2 rounded">
            Example: Score 83 → WARN, Score 68 → FAIL
          </p>

            {/* On FAIL Actions */}
            <div>
              <label className={labelClass}>On FAIL Actions</label>
              <div className="space-y-2 mt-2">
                {[
                  { key: 'blockResponse', label: 'Block response to user', desc: 'Use for regulated or strict production workflows' },
                  { key: 'logIncident', label: 'Log incident', desc: 'Stores evaluation + flags for monitoring & audit' },
                  { key: 'notifyAdmin', label: 'Notify Admin / RM', desc: 'Route to ops channel or ticketing integration' },
                  { key: 'attachWarningBanner', label: 'Attach warning banner', desc: 'Shows "Quality warning" without blocking the flow' }
                ].map(action => (
                  <div key={action.key} className="flex items-start gap-2">
                    <button
                      onClick={() => updateConfig({
                        onFail: { ...config.onFail, [action.key]: !config.onFail[action.key as keyof typeof config.onFail] }
                      })}
                      className={`relative w-8 h-4 rounded-full transition-colors ${config.onFail[action.key as keyof typeof config.onFail] ? 'bg-sky-500' : 'bg-slate-300'
                        }`}
                    >
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${config.onFail[action.key as keyof typeof config.onFail] ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                    </button>
                    <div>
                      <span className="text-[10px] font-semibold text-slate-700">{action.label}</span>
                      <p className="text-[9px] text-slate-400">{action.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          <div className="p-2 bg-amber-50 border border-amber-100 rounded text-[9px] text-amber-700">
            <strong>Demo tip:</strong> Keep Block OFF but show banner + audit ON.
          </div>
        </div>
      )}

      {/* Ground Truth Section (Hybrid Mode) */}
      {config.mode === 'hybrid' && (
        <>
          <SectionHeader title="Groundedness Config" expanded={showGroundedness} onToggle={() => setShowGroundedness(!showGroundedness)} />
          {showGroundedness && (
            <div className="p-3 bg-slate-50 rounded border border-slate-100 space-y-3 mt-1">
              <InputGroup label="Ground Truth (Optional)">
                <textarea
                  className={`${inputClass} font-mono text-[10px]`}
                  rows={3}
                  placeholder="Enter the ideal/expected answer for comparison..."
                  value={config.groundTruth || ''}
                  onChange={(e) => updateConfig({ groundTruth: e.target.value })}
                />
              </InputGroup>
              <p className="text-[9px] text-slate-400">
                If provided, responses will be compared against this ground truth for semantic similarity scoring.
              </p>
              <InputGroup label="Judge Model">
                <select
                  className={inputClass}
                  value={config.judgeModel || 'gemini-3-pro-preview'}
                  onChange={(e) => updateConfig({ judgeModel: e.target.value })}
                >
                  <option value="gemini-3-pro-preview">Gemini 3 Pro (Default)</option>
                  <option value="gemini-2.5-flash-preview-05-20">Gemini 2.5 Flash</option>
                  <option value="gemini-2.5-pro-preview-05-06">Gemini 2.5 Pro</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash (Fastest)</option>
                </select>
              </InputGroup>
              <p className="text-[9px] text-slate-400">
                AI model used for quality evaluation. Flash models are faster, Pro models are more accurate.
              </p>
            </div>
          )}
        </>
      )}

      {/* Testing Section */}
      <div className="border-t border-slate-100 my-4"></div>
      <div className="mb-2 text-[10px] font-bold text-sky-600 uppercase tracking-wide">Testing</div>

      {/* Test Mode Toggle */}
      <div className="mb-3">
        <div className="flex gap-1 p-0.5 bg-slate-100 rounded-lg">
          <button
            onClick={() => setTestMode('agent')}
            className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all ${testMode === 'agent' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-400'}`}
          >
            🤖 Agent
          </button>
          <button
            onClick={() => setTestMode('workflow')}
            className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all ${testMode === 'workflow' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
          >
            🔄 Workflow
          </button>
        </div>
        <p className="text-[8px] text-slate-400 mt-0.5">
          {testMode === 'agent' ? 'Evaluate single agent' : 'Run pipeline → evaluate'}
        </p>
      </div>

      {/* Target Agent - only for agent mode */}
      {testMode === 'agent' && (
      <InputGroup label="Target Agent">
        <select
          className={inputClass}
          value={config.targetAgent || ''}
          onChange={(e) => updateConfig({ targetAgent: e.target.value })}
        >
          <option value="">{connectedAgent ? `Auto — ${connectedAgent}` : 'Auto (first deployed)'}</option>
          {deployedAgents.map(a => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        {connectedAgent && !config.targetAgent && (
          <p className="text-[9px] text-emerald-600 mt-0.5">✓ Detected from workflow</p>
        )}
      </InputGroup>
      )}

      {/* Workflow agents - only for workflow mode */}
      {testMode === 'workflow' && (
        <div className="mb-2 p-2 bg-indigo-50 border border-indigo-100 rounded text-[9px]">
          {workflowRootAgent && (
            <div className="mb-1"><span className="font-bold text-indigo-800">Root:</span>{' '}<span className="font-bold text-indigo-700">{workflowRootAgent}</span></div>
          )}
          <span className="text-indigo-500">Same as main chat UI — root agent orchestrates all sub-agents.</span>
        </div>
      )}

      <InputGroup label="Test Query">
        <textarea 
          className={`${inputClass} bg-sky-50/30 border-sky-100`}
          rows={2}
          placeholder="Type your query like a normal chat message..."
          value={data.testInput || ''}
          onChange={(e) => onChange({ testInput: e.target.value })}
        />
      </InputGroup>

      <InputGroup label="Output">
        <textarea 
          className={`${inputClass} bg-slate-50 text-slate-500`}
          rows={2}
          placeholder="Test results will appear here..."
          value={data.testOutput || ''}
          readOnly
        />
      </InputGroup>

        {/* Evaluation Results Display */}
        {data.evaluationResults && (
          <div className="mt-3 p-3 bg-slate-50 rounded border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-slate-600">Evaluation Results</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${data.evaluationResults.overallAssessment.verdict === 'PASS' ? 'bg-emerald-100 text-emerald-700' :
                data.evaluationResults.overallAssessment.verdict === 'WARN' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                {data.evaluationResults.overallAssessment.verdict} - {data.evaluationResults.overallAssessment.finalScore.toFixed(1)}%
              </span>
            </div>
            <p className="text-[9px] text-slate-500 mb-2">
              Mode: {data.evaluationResults.meta.evaluationMode}
            </p>
            {Object.entries(data.evaluationResults.results).slice(0, 3).map(([metric, result]) => (
              <div key={metric} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                <span className="text-[9px] text-slate-600">{metric}</span>
                <span className={`text-[9px] font-bold ${result.pass ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(result.score * 100).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Test Button */}
        <button
          onClick={runEvaluationTest}
          disabled={testLoading}
          className={`w-full mt-3 px-3 py-2 rounded text-xs font-semibold transition-all flex items-center justify-center gap-2 ${testLoading
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100'
        }`}
      >
        {testLoading ? (
          <>
            <span className="w-3 h-3 border-2 border-sky-300 border-t-sky-600 rounded-full animate-spin"></span>
            Running Evaluation...
          </>
        ) : (
          <>🧪 Run Test</>
        )}
      </button>

      <div className="mt-3 p-2 bg-sky-50 border border-sky-100 rounded text-[10px] text-sky-700">
        Evaluation results will be logged to <strong>Monitoring & Audit</strong> page.
      </div>
    </div>
    </>
  );
};

export const DataConnectorNode = ({ data, onChange }: { data: NodeData, onChange: (d: Partial<NodeData>) => void }) => {
  const [connectionStatus, setConnectionStatus] = useState<{connected: boolean; email?: string} | null>(null);
  const [checking, setChecking] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [targets, setTargets] = useState<Array<{id: string; name: string; detail?: string}>>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [error, setError] = useState('');
  const [chunkKey, setChunkKey] = useState('');
  const [chunkStatus, setChunkStatus] = useState<any>(data.chunkStatus || null);
  const [chunking, setChunking] = useState(false);

  const isOAuth = data.connectorProvider === 'google_sheets' || data.connectorProvider === 'google_drive' || data.connectorProvider === 'sharepoint';

  // Check connection status on mount and when provider changes
  useEffect(() => {
    if (!isOAuth) {
      setConnectionStatus(null);
      setTargets([]);
      return;
    }
    checkStatus();
  }, [data.connectorProvider]);

  // Listen for OAuth callback
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'oauth_success') {
        const provider = event.data.provider;
        if (provider === data.connectorProvider || 
            (provider === 'google_sheets' && (data.connectorProvider === 'google_sheets' || data.connectorProvider === 'google_drive')) ||
            (provider === 'sharepoint' && data.connectorProvider === 'sharepoint')) {
          onChange({ connectorCredentials: 'oauth_connected' });
          setConnecting(false);
          // Re-check status after a short delay
          setTimeout(() => checkStatus(), 500);
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [data.connectorProvider]);

  const checkStatus = async () => {
    setChecking(true);
    setError('');
    try {
      if (data.connectorProvider === 'google_sheets' || data.connectorProvider === 'google_drive') {
        const { getGoogleStatus } = await import('../api');
        const status = await getGoogleStatus();
        setConnectionStatus(status);
        if (status.connected) {
          onChange({ connectorCredentials: 'oauth_connected' });
          loadTargets();
        }
      } else if (data.connectorProvider === 'sharepoint') {
        const { getMicrosoftStatus } = await import('../api');
        const status = await getMicrosoftStatus();
        setConnectionStatus(status);
        if (status.connected) {
          onChange({ connectorCredentials: 'oauth_connected' });
          loadTargets();
        }
      }
    } catch (e: any) {
      setError(e.message || 'Status check failed');
    } finally {
      setChecking(false);
    }
  };

  const startOAuth = async () => {
    setConnecting(true);
    setError('');
    try {
      if (data.connectorProvider === 'google_sheets' || data.connectorProvider === 'google_drive') {
        const { getGoogleAuthUrl } = await import('../api');
        const authUrl = await getGoogleAuthUrl();
        window.open(authUrl, 'oauthPopup', 'width=600,height=700,scrollbars=yes');
      } else if (data.connectorProvider === 'sharepoint') {
        const { getMicrosoftAuthUrl } = await import('../api');
        const authUrl = await getMicrosoftAuthUrl();
        window.open(authUrl, 'oauthPopup', 'width=600,height=700,scrollbars=yes');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to start authentication');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      if (data.connectorProvider === 'google_sheets' || data.connectorProvider === 'google_drive') {
        const { disconnectGoogle } = await import('../api');
        await disconnectGoogle();
      } else if (data.connectorProvider === 'sharepoint') {
        const { disconnectMicrosoft } = await import('../api');
        await disconnectMicrosoft();
      }
      setConnectionStatus({ connected: false });
      setTargets([]);
      onChange({ connectorCredentials: '', connectorTarget: '' });
    } catch (e: any) {
      setError(e.message || 'Disconnect failed');
    }
  };

  const loadTargets = async () => {
    setLoadingTargets(true);
    setError('');
    try {
      if (data.connectorProvider === 'google_sheets') {
        const { listGoogleSheets } = await import('../api');
        const sheets = await listGoogleSheets();
        setTargets(sheets.map(s => ({ id: s.id, name: s.name, detail: s.owner })));
      } else if (data.connectorProvider === 'google_drive') {
        const { listGoogleFolders } = await import('../api');
        const folders = await listGoogleFolders();
        setTargets(folders.map(f => ({ id: f.id, name: f.name, detail: f.owner })));
      } else if (data.connectorProvider === 'sharepoint') {
        const { listSharePointSites } = await import('../api');
        const sites = await listSharePointSites();
        setTargets(sites.map(s => ({ id: s.id, name: s.name, detail: s.webUrl })));
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load targets');
    } finally {
      setLoadingTargets(false);
    }
  };

  const isConnected = connectionStatus?.connected || data.connectorCredentials === 'oauth_connected';

  return (
    <div>
      <div className="mb-4 text-xs text-slate-500 italic">
        Connect external databases and configure retrieval strategy.
      </div>

      <InputGroup label="Select Provider">
        <select
          className={inputClass}
          value={data.connectorProvider || 'postgres'}
          onChange={(e) => {
            onChange({ connectorProvider: e.target.value as any, connectorCredentials: '', connectorTarget: '' });
            setConnectionStatus(null);
            setTargets([]);
            setError('');
          }}
        >
          <option value="postgres">PostgreSQL</option>
          <option value="sharepoint">SharePoint</option>
          <option value="google_sheets">Google Sheets</option>
          <option value="google_drive">Google Drive</option>
          <option value="oracle">Oracle</option>
        </select>
      </InputGroup>

      <InputGroup label="Authentication">
        {isOAuth ? (
          <div className="space-y-2">
            {/* Connection Status */}
            {checking && (
              <div className="flex items-center gap-2 text-xs text-slate-500 py-1.5 px-3">
                <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
                Checking connection...
              </div>
            )}

            {!checking && isConnected ? (
              <>
                {/* Connected State */}
                <div className="flex items-center justify-between py-2 px-3 bg-emerald-50 border border-emerald-200 rounded text-xs">
                  <div className="flex items-center gap-2">
                    <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
                    <div>
                      <span className="font-medium text-emerald-700">
                        Connected to {data.connectorProvider?.startsWith('google') ? 'Google' : 'Microsoft'}
                      </span>
                      {connectionStatus?.email && (
                        <p className="text-[10px] text-emerald-600 mt-0.5">{connectionStatus.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={disconnect}
                    className="text-[10px] text-red-500 hover:text-red-700 font-medium hover:underline"
                  >
                    Disconnect
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Not Connected State */}
                <button
                  onClick={startOAuth}
                  disabled={connecting}
                  className={`w-full text-xs py-2 px-3 flex items-center justify-center gap-2 border rounded font-medium transition-all ${
                    connecting
                      ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                      : data.connectorProvider?.startsWith('google')
                        ? 'bg-white border-slate-300 hover:bg-blue-50 hover:border-blue-300 text-slate-700'
                        : 'bg-white border-slate-300 hover:bg-blue-50 hover:border-blue-300 text-slate-700'
                  }`}
                >
                  {connecting ? (
                    <>
                      <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
                      Waiting for sign-in...
                    </>
                  ) : (
                    <>
                      {data.connectorProvider?.startsWith('google') ? (
                        <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 23 23" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#f35325" d="M1 1h10v10H1z"/>
                          <path fill="#81bc06" d="M12 1h10v10H12z"/>
                          <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                          <path fill="#ffba08" d="M12 12h10v10H12z"/>
                        </svg>
                      )}
                      Connect {data.connectorProvider?.startsWith('google') ? 'Google' : 'Microsoft'} Account
                    </>
                  )}
                </button>
                {connecting && (
                  <p className="text-[9px] text-slate-400 text-center">
                    Complete sign-in in the popup window
                  </p>
                )}
              </>
            )}

            {error && (
              <p className="text-[10px] text-red-500 mt-1 bg-red-50 border border-red-100 rounded px-2 py-1">{error}</p>
            )}
          </div>
        ) : (
          <div className="space-y-2 mt-1">
            <input
              type="text"
              className={inputClass}
              placeholder="Host (e.g. db.xxx.supabase.co or IP)"
              value={(() => { try { return JSON.parse(data.connectorCredentials || '{}').host || ''; } catch { return ''; } })()}
              onChange={(e) => {
                try {
                  const creds = JSON.parse(data.connectorCredentials || '{}');
                  creds.host = e.target.value;
                  onChange({ connectorCredentials: JSON.stringify(creds) });
                } catch {}
              }}
            />
            <div className="flex gap-2">
              <input
                type="text"
                className={inputClass}
                placeholder="Database name"
                value={(() => { try { return JSON.parse(data.connectorCredentials || '{}').dbname || ''; } catch { return ''; } })()}
                onChange={(e) => {
                  try {
                    const creds = JSON.parse(data.connectorCredentials || '{}');
                    creds.dbname = e.target.value;
                    onChange({ connectorCredentials: JSON.stringify(creds) });
                  } catch {}
                }}
              />
              <input
                type="number"
                className={inputClass}
                placeholder="Port"
                style={{ width: '80px' }}
                value={(() => { try { return JSON.parse(data.connectorCredentials || '{}').port || '5432'; } catch { return '5432'; } })()}
                onChange={(e) => {
                  try {
                    const creds = JSON.parse(data.connectorCredentials || '{}');
                    creds.port = parseInt(e.target.value) || 5432;
                    onChange({ connectorCredentials: JSON.stringify(creds) });
                  } catch {}
                }}
              />
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className={inputClass}
                placeholder="User"
                value={(() => { try { return JSON.parse(data.connectorCredentials || '{}').user || ''; } catch { return ''; } })()}
                onChange={(e) => {
                  try {
                    const creds = JSON.parse(data.connectorCredentials || '{}');
                    creds.user = e.target.value;
                    onChange({ connectorCredentials: JSON.stringify(creds) });
                  } catch {}
                }}
              />
              <input
                type="password"
                className={inputClass}
                placeholder="Password"
                value={(() => { try { return JSON.parse(data.connectorCredentials || '{}').password || ''; } catch { return ''; } })()}
                onChange={(e) => {
                  try {
                    const creds = JSON.parse(data.connectorCredentials || '{}');
                    creds.password = e.target.value;
                    onChange({ connectorCredentials: JSON.stringify(creds) });
                  } catch {}
                }}
              />
            </div>

            {/* Test Connection Button */}
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                setChecking(true);
                setError('');
                try {
                  let creds: any = {};
                  try {
                    creds = JSON.parse(data.connectorCredentials || '{}');
                  } catch {
                    setError('Invalid credentials format. Please fill all fields.');
                    setChecking(false);
                    return;
                  }
                  if (!creds.host || !creds.user) {
                    setError('Please fill in Host and User fields first.');
                    setChecking(false);
                    return;
                  }
                  const { testDBConnection } = await import('../api');
                  const result = await testDBConnection(creds);
                  if (result.success) {
                    setConnectionStatus({ connected: true, email: result.message });
                    setError('');
                    // Auto-load tables
                    const { listDBTables } = await import('../api');
                    const tablesResult = await listDBTables(creds);
                    if (tablesResult.success && tablesResult.tables) {
                      setTargets(tablesResult.tables.map((t: any) => ({
                        id: t.name,
                        name: t.name,
                        detail: `${t.rows} rows · ${t.column_count} cols · ${t.size}`,
                      })));
                    }
                  } else {
                    setConnectionStatus({ connected: false });
                    setError(result.message || 'Connection failed');
                  }
                } catch (err: any) {
                  setError(err.message || 'Test connection failed');
                  setConnectionStatus({ connected: false });
                } finally {
                  setChecking(false);
                }
              }}
              disabled={checking}
              className={`w-full text-xs py-2 px-3 flex items-center justify-center gap-2 border rounded font-medium transition-all cursor-pointer ${
                checking
                  ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-wait'
                  : connectionStatus?.connected
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                    : 'bg-blue-50 border-blue-300 hover:bg-blue-100 text-blue-700 hover:shadow-sm'}`}
            >
              {checking ? (
                <><span className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span> Testing connection...</>
              ) : connectionStatus?.connected ? (
                <><CheckIcon className="w-3 h-3" /> Connected — Test Again</>
              ) : (
                <>🔌 Test Connection</>
              )}
            </button>

            {/* Connection Result */}
            {connectionStatus?.connected && connectionStatus.email && (
              <div className="text-[10px] bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-emerald-700">
                ✅ {connectionStatus.email}
              </div>
            )}
            {error && (
              <p className="text-[10px] text-red-500 bg-red-50 border border-red-100 rounded px-2 py-1">{error}</p>
            )}
          </div>
        )}
      </InputGroup>

      <InputGroup label={`Discovered Targets${targets.length > 0 ? ` (${targets.length} ${isOAuth ? (data.connectorProvider === 'google_sheets' ? 'sheets' : (data.connectorProvider === 'google_drive' ? 'folders' : 'sites')) : 'tables'} found)` : ''}`}>
        {/* OAuth providers (Google Sheets / SharePoint) */}
        {isOAuth && isConnected && targets.length > 0 ? (
          <div className="space-y-1.5">
            {/* Select All / Deselect All controls */}
            <div className="flex items-center justify-between px-1 pb-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setChunkStatus(null); onChange({ selectedTargets: targets.map(t => t.id), chunkStatus: null }); }}
                  className={`text-[9px] font-medium px-2 py-0.5 rounded transition-all ${
                    (data.selectedTargets || []).length === targets.length
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                  }`}
                >
                  ✅ Select All
                </button>
                <button
                  onClick={() => { setChunkStatus(null); onChange({ selectedTargets: [], chunkStatus: null }); }}
                  className="text-[9px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-600 transition-all"
                >
                  ✕ Deselect All
                </button>
              </div>
              <span className="text-[9px] text-slate-400">
                {(data.selectedTargets || []).length}/{targets.length} selected
              </span>
            </div>
            {/* Targets list with checkboxes */}
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
              {targets.map(t => {
                const isSelected = (data.selectedTargets || []).includes(t.id);
                const isChunked = (data.chunkedTables || []).includes(t.id);
                return (
                <div
                  key={t.id}
                  onClick={() => {
                    const current = data.selectedTargets || [];
                    const updated = isSelected
                      ? current.filter(id => id !== t.id)
                      : [...current, t.id];
                    setChunkStatus(null);
                    onChange({ selectedTargets: updated, connectorTarget: updated[0] || '', chunkStatus: null });
                  }}
                  className={`px-2 py-1.5 transition-colors text-[10px] cursor-pointer ${
                    isSelected
                      ? 'bg-teal-50 border-l-2 border-l-teal-500'
                      : isChunked
                        ? 'bg-emerald-50/50 border-l-2 border-l-emerald-300'
                        : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-3 h-3 cursor-pointer"
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <span className="font-medium text-slate-800">{t.name}</span>
                      {isChunked && (
                        <span className="text-emerald-600 flex items-center gap-1 text-[9px]"><CheckIcon className="w-3 h-3" /> Indexed</span>
                      )}
                    </div>
                  </div>
                  {t.detail && (
                    <span className="text-[9px] text-slate-400 ml-5">{t.detail}</span>
                  )}
                </div>
              )})}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-teal-700 font-medium">
                {(data.selectedTargets || []).length === 0
                  ? `Select ${data.connectorProvider === 'google_sheets' ? 'spreadsheets' : (data.connectorProvider === 'google_drive' ? 'folders' : 'sites')} to chunk`
                  : (data.selectedTargets || []).length === targets.length
                    ? `All ${data.connectorProvider === 'google_sheets' ? 'spreadsheets' : (data.connectorProvider === 'google_drive' ? 'folders' : 'sites')} selected`
                    : `${(data.selectedTargets || []).length} ${data.connectorProvider === 'google_sheets' ? 'spreadsheet(s)' : (data.connectorProvider === 'google_drive' ? 'folder(s)' : 'site(s)')} selected`}
              </span>
              <button
                onClick={loadTargets}
                disabled={loadingTargets}
                className="text-[9px] text-blue-600 hover:underline flex items-center gap-1"
              >
                {loadingTargets ? (
                  <><span className="w-2 h-2 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></span> Loading...</>
                ) : (
                  '↻ Refresh list'
                )}
              </button>
            </div>
          </div>
        ) : isOAuth && isConnected && loadingTargets ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-1.5">
            <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
            Loading available {data.connectorProvider === 'google_sheets' ? 'spreadsheets' : (data.connectorProvider === 'google_drive' ? 'folders' : 'sites')}...
          </div>
        ) : isOAuth && !isConnected ? (
          <p className="text-[10px] text-slate-400 py-1 italic">Connect your account first to select a target</p>

        /* PostgreSQL / Oracle — show tables after Test Connection */
        ) : !isOAuth && targets.length > 0 ? (
          <div className="space-y-1.5">
            {/* Select All / Deselect All controls */}
            <div className="flex items-center justify-between px-1 pb-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onChange({ selectedTargets: targets.map(t => t.id) })}
                  className={`text-[9px] font-medium px-2 py-0.5 rounded transition-all ${
                    (data.selectedTargets || []).length === targets.length
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                  }`}
                >
                  ✅ Select All
                </button>
                <button
                  onClick={() => { setChunkStatus(null); onChange({ selectedTargets: [], chunkStatus: null }); }}
                  className="text-[9px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-600 transition-all"
                >
                  ✕ Deselect All
                </button>
              </div>
              <span className="text-[9px] text-slate-400">
                {(data.selectedTargets || []).length}/{targets.length} selected
              </span>
            </div>
            {/* Table list as scrollable cards with checkboxes */}
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
              {targets.map(t => {
                const isSelected = (data.selectedTargets || []).includes(t.id);
                const isChunked = (data.chunkedTables || []).includes(t.id);
                return (
                <div
                  key={t.id}
                  onClick={() => {
                    const current = data.selectedTargets || [];
                    const updated = isSelected
                      ? current.filter(id => id !== t.id)
                      : [...current, t.id];
                    setChunkStatus(null);
                    onChange({ selectedTargets: updated, chunkStatus: null });
                  }}
                  className={`px-2 py-1.5 transition-colors text-[10px] cursor-pointer ${
                    isSelected
                      ? 'bg-teal-50 border-l-2 border-l-teal-500'
                      : isChunked
                        ? 'bg-emerald-50/50 border-l-2 border-l-emerald-300'
                        : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-3 h-3 cursor-pointer"
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <span className="font-medium text-slate-800 font-mono">{t.name}</span>
                      {isChunked && (
                        <span className="text-emerald-600 flex items-center gap-1 text-[9px]"><CheckIcon className="w-3 h-3" /> Indexed</span>
                      )}
                    </div>
                  </div>
                  {t.detail && (
                    <span className="text-[9px] text-slate-400 ml-5">{t.detail}</span>
                  )}
                </div>
              )})}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-teal-700 font-medium">
                {(data.selectedTargets || []).length === 0
                  ? 'Select tables to chunk'
                  : (data.selectedTargets || []).length === targets.length
                    ? 'All tables selected'
                    : `${(data.selectedTargets || []).length} table(s) selected`}
              </span>
              <button
                onClick={async () => {
                  setLoadingTargets(true);
                  try {
                    const creds = JSON.parse(data.connectorCredentials || '{}');
                    const { listDBTables } = await import('../api');
                    const result = await listDBTables(creds);
                    if (result.success && result.tables) {
                      setTargets(result.tables.map((t: any) => ({
                        id: t.name,
                        name: t.name,
                        detail: `${t.rows} rows · ${t.column_count} cols · ${t.size}`,
                      })));
                    }
                  } catch {} finally { setLoadingTargets(false); }
                }}
                disabled={loadingTargets}
                className="text-[9px] text-blue-600 hover:underline flex items-center gap-1"
              >
                {loadingTargets ? (
                  <><span className="w-2 h-2 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></span> Refreshing...</>
                ) : '↻ Refresh tables'}
              </button>
            </div>
          </div>
        ) : !isOAuth && !connectionStatus?.connected ? (
          <p className="text-[10px] text-slate-400 py-1 italic">Test your connection first to see available tables</p>
        ) : (
          <input
            type="text"
            className={inputClass}
            placeholder="e.g. schema.table, folder path"
            value={data.connectorTarget || ''}
            onChange={(e) => onChange({ connectorTarget: e.target.value })}
          />
        )}
      </InputGroup>

      <InputGroup label="Processing Strategy">
        <select
          className={inputClass}
          value={data.connectorStrategy || 'vector_db'}
          onChange={(e) => onChange({ connectorStrategy: e.target.value as any })}
        >
          <option value="vector_db">Vector DB (Vertex AI Search)</option>
          <option value="knowledge_graph">Knowledge Graph</option>
          <option value="live_query">Live Query Only (No Indexing)</option>
        </select>
      </InputGroup>

      {/* Chunking Pipeline — shown for ALL providers when Vector DB is selected */}
      {(data.connectorStrategy || 'vector_db') === 'vector_db' && (
        <InputGroup label="Vector Indexing Status">
          <div className="space-y-2">
            {/* Start / Re-index button */}
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isConnected && !connectionStatus?.connected) {
                  setError(isOAuth ? 'Connect your account first!' : 'Test your connection first!');
                  return;
                }

                // Determine what to chunk based on provider type
                let itemsToChunk: string[] = [];
                let itemNames: string[] = [];

                if (isOAuth) {
                  // OAuth providers: use selectedTargets or the single selected target
                  const selected = data.selectedTargets || [];
                  if (selected.length > 0) {
                    itemsToChunk = selected;
                    itemNames = selected.map(id => targets.find(t => t.id === id)?.name || id);
                  } else if (data.connectorTarget) {
                    itemsToChunk = [data.connectorTarget];
                    itemNames = [targets.find(t => t.id === data.connectorTarget)?.name || data.connectorTarget];
                  } else {
                    setError('Select at least one target to index!');
                    return;
                  }
                  // Filter out already chunked
                  const alreadyChunked = data.chunkedTables || [];
                  itemsToChunk = itemsToChunk.filter(id => !alreadyChunked.includes(id));
                  itemNames = itemsToChunk.map(id => targets.find(t => t.id === id)?.name || id);
                } else {
                  // DB providers: use selectedTargets or all targets
                  const selected = data.selectedTargets || [];
                  if (selected.length > 0) {
                    itemsToChunk = selected.filter(t => !(data.chunkedTables || []).includes(t));
                  } else {
                    itemsToChunk = targets.map(t => t.id).filter(t => !(data.chunkedTables || []).includes(t));
                  }
                  itemNames = itemsToChunk;
                }

                if (itemsToChunk.length === 0) {
                  setError('All selected items are already indexed!');
                  return;
                }

                setChunking(true);
                setChunkStatus(null);
                try {
                  if (isOAuth) {
                    // Use OAuth chunking API
                    const { startOAuthChunking } = await import('../api');
                    const result = await startOAuthChunking({
                      provider: data.connectorProvider as 'google_sheets' | 'sharepoint',
                      targets: itemsToChunk,
                      target_names: itemNames,
                    });
                    if (result.key) {
                      setChunkKey(result.key);
                      setChunkStatus(result);
                      onChange({ chunkStatus: result });
                      const poll = setInterval(async () => {
                        const { getOAuthChunkStatus } = await import('../api');
                        const status = await getOAuthChunkStatus(result.key!);
                        setChunkStatus(status);
                        onChange({ chunkStatus: status });
                        if (status.status === 'done' || status.status === 'error') {
                          clearInterval(poll);
                          setChunking(false);
                          if (status.status === 'done') {
                            onChange({
                              chunkedTables: Array.from(new Set([...(data.chunkedTables || []), ...itemsToChunk])),
                              chunkStatus: status
                            });
                          }
                        }
                      }, 2000);
                    } else {
                      setChunking(false);
                    }
                  } else {
                    // Use DB chunking API
                    const creds = JSON.parse(data.connectorCredentials || '{}');
                    const { startChunking } = await import('../api');
                    const result = await startChunking({ ...creds, tables: itemsToChunk });
                    if (result.key) {
                      setChunkKey(result.key);
                      setChunkStatus(result);
                      onChange({ chunkStatus: result });
                      const poll = setInterval(async () => {
                        const { getChunkStatus } = await import('../api');
                        const status = await getChunkStatus(result.key!);
                        setChunkStatus(status);
                        onChange({ chunkStatus: status });
                        if (status.status === 'done' || status.status === 'error') {
                          clearInterval(poll);
                          setChunking(false);
                          if (status.status === 'done') {
                            onChange({
                              chunkedTables: Array.from(new Set([...(data.chunkedTables || []), ...itemsToChunk])),
                              chunkStatus: status
                            });
                          }
                        }
                      }, 2000);
                    } else {
                      setChunking(false);
                    }
                  }
                } catch (err: any) {
                  setError(err.message);
                  setChunking(false);
                }
              }}
              disabled={chunking || (!isConnected && !connectionStatus?.connected)}
              className={`w-full text-xs py-2 px-3 flex items-center justify-center gap-2 border rounded font-medium transition-all cursor-pointer ${
                chunking
                  ? 'bg-purple-50 text-purple-500 border-purple-200 cursor-wait'
                  : chunkStatus?.status === 'done' && chunkStatus?.total_chunks > 0
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                    : (!isConnected && !connectionStatus?.connected)
                      ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                      : 'bg-purple-50 border-purple-300 hover:bg-purple-100 text-purple-700'
              }`}
            >
              {chunking ? (
                <><span className="w-3 h-3 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></span> Indexing...</>
              ) : chunkStatus?.status === 'done' && chunkStatus?.total_chunks > 0 ? (
                <>✅ {chunkStatus.total_chunks} chunks indexed — Re-index</>
              ) : (
                <>🔄 Start Vector Indexing{isOAuth && data.connectorTarget ? ` (${targets.find(t => t.id === data.connectorTarget)?.name || 'Selected'})` : ''}</>
              )}
            </button>

            {/* Progress Bar */}
            {chunkStatus && (chunkStatus.status === 'running' || chunkStatus.status === 'started') && (
              <div className="space-y-1">
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500"
                    style={{ width: `${chunkStatus.progress || 0}%` }}
                  />
                </div>
                <p className="text-[9px] text-purple-600 font-medium">
                  {chunkStatus.progress || 0}% — {chunkStatus.message || 'Processing...'}
                </p>
              </div>
            )}

            {/* Done status */}
            {chunkStatus?.status === 'done' && chunkStatus?.total_chunks > 0 && !chunking && (
              <div className="text-[10px] bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-emerald-700">
                ✅ {chunkStatus.message}
              </div>
            )}

            {/* Error */}
            {chunkStatus?.status === 'error' && (
              <p className="text-[10px] text-red-500 bg-red-50 border border-red-100 rounded px-2 py-1">
                {chunkStatus.message}
              </p>
            )}

            {/* Not connected hint */}
            {!isConnected && !connectionStatus?.connected && (
              <p className="text-[9px] text-slate-400 italic">
                {isOAuth ? 'Connect your account first to enable indexing' : 'Test your database connection first to enable indexing'}
              </p>
            )}
          </div>
        </InputGroup>
      )}

      <div className="mt-4 space-y-2">
        <label className="flex items-center space-x-2 text-[10px] font-medium text-slate-700">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={!!data.allowLiveRead}
            onChange={(e) => onChange({ allowLiveRead: e.target.checked })}
          />
          <span>Allow Agent to Live Read</span>
        </label>
        <label className="flex items-center space-x-2 text-[10px] font-medium text-slate-700">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={!!data.allowLiveWrite}
            onChange={(e) => onChange({ allowLiveWrite: e.target.checked })}
          />
          <span>Allow Agent to Live Write</span>
        </label>
      </div>

      {/* Auto-wired tools indicator */}
      {isConnected && (data.allowLiveRead || data.allowLiveWrite) && (
        <div className="mt-3 p-2 bg-teal-50 border border-teal-100 rounded">
          <p className="text-[9px] font-bold text-teal-700 mb-1">Auto-Wired MCP Tools:</p>
          <div className="flex flex-wrap gap-1">
            {data.connectorStrategy === 'vector_db' && (
              <span className="text-[8px] px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full">search_vector_database</span>
            )}
            {data.connectorStrategy === 'knowledge_graph' && (
              <span className="text-[8px] px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full">query_knowledge_graph</span>
            )}
            {data.allowLiveRead && data.connectorProvider === 'google_sheets' && (
              <span className="text-[8px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">read_sheet_range</span>
            )}
            {data.allowLiveWrite && data.connectorProvider === 'google_sheets' && (
              <span className="text-[8px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">append_sheet_row</span>
            )}
            {data.allowLiveRead && data.connectorProvider === 'sharepoint' && (
              <>
                <span className="text-[8px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">list_sharepoint_files</span>
                <span className="text-[8px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">read_sharepoint_document_live</span>
              </>
            )}
            {data.allowLiveRead && (data.connectorProvider === 'postgres' || data.connectorProvider === 'oracle') && (
              <span className="text-[8px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">list_database_tables</span>
            )}
            {data.allowLiveWrite && (data.connectorProvider === 'postgres' || data.connectorProvider === 'oracle') && (
              <span className="text-[8px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">execute_live_sql_query</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};